import {
  APP_NAME,
  CULTURA_CONTEXT,
  DEFAULT_PRIORITY,
  FABRICANTE_REFERENCIAS,
  PRIORITY_BY_TAG,
  TAG_PATTERNS
} from '../data/knowledgeBase';
import { AGRO_CHALLENGE_SIGNALS, type AgroChallengeSignal } from '../data/agroChallenges';
import { PRODUCT_INTELLIGENCE_DB, type ProductIntelligence } from '../data/productIntelligence';
import { PLAYER_VALUE_PLAYBOOKS, type PlayerValuePlaybook } from '../data/playerValuePlaybooks';
import {
  VALUE_SELLING_GLOBAL_RULES,
  CROP_BENCHMARKS,
  ROI_SCENARIO_MULTIPLIERS,
  YARA_CROP_FOCUS,
  YARA_LINE_GAIN_PERCENT,
  YARA_LINE_ROLE_MAP,
  YARA_OBJECTIVE_TO_LINES,
  YARA_PROBLEM_TO_LINES,
  YARA_STAGE_TO_LINES
} from '../data/valueSellingKnowledge';
import { buildCortevaValueHooks, inferCortevaSolutionsByScenario } from '../data/cortevaSolutions';
import { inferAgroScenarioName, inferSpinScenarioTemplate } from '../data/spinScenarioLibrary';
import { getEpaScenarioTemplateById } from '../data/epaScenarioLibrary';
import { getAgroScenarioPlaybook } from '../data/agroScenarioPlaybooks';
import { getAgroScenarioOpcPlaybook } from '../data/agroScenarioOpcPlaybook';
import { getAgroScenarioEpaPlaybook } from '../data/agroScenarioEpaPlaybook';
import type {
  PreparationInput,
  PreparationResult,
  ProducerBehaviorProfile,
  ProductReference,
  ScenarioAnalysis,
  SpinBlock,
  ValueSineMap
} from '../types';
import { normalizeText, pick, unique } from '../utils/text';

type Theme = ScenarioAnalysis['primaryTheme'];
type StrategicHooks = PlayerValuePlaybook['hooks'];
type ProfileSection = 'opc' | 'spin' | 'epa' | 'sine';

const PROFILE_LABELS: Record<ProducerBehaviorProfile, string> = {
  analitico: 'Analítico',
  diretivo: 'Diretivo',
  integrador: 'Integrador',
  expressivo: 'Expressivo'
};

const PROFILE_PLAYBOOKS: Record<
  ProducerBehaviorProfile,
  {
    titulo: string;
    linguagem: string[];
    postura: string[];
    ritmo: string[];
    fechamento: string[];
    opc: string[];
    spinSituacao: string[];
    spinProblema: string[];
    spinImplicacao: string[];
    spinNecessidade: string[];
    epa: string[];
    sine: string[];
  }
> = {
  analitico: {
    titulo: 'Abordagem lógica, precisa e orientada por evidência',
    linguagem: [
      'Use linguagem estruturada, comparativos, números e evidências técnicas da área.',
      'Evite floreio, excesso de emoção e afirmações sem base de campo.'
    ],
    postura: [
      'Ganhe confiança mostrando critério, método e clareza de raciocínio.',
      'Dê espaço para o produtor analisar antes de avançar para a decisão.'
    ],
    ritmo: ['Conduza a conversa em sequência lógica: contexto, dado, implicação, decisão.', 'Vá em profundidade quando o produtor pedir mais detalhe técnico.'],
    fechamento: ['Feche com critério de validação, indicador, data de leitura e regra clara de decisão.'],
    opc: [
      'Estruture a conversa com começo, meio e fim muito claros, sempre apoiando o próximo passo em evidência.',
      'Mostre lógica de decisão e evite saltar para a solução sem consolidar o raciocínio.'
    ],
    spinSituacao: ['Quais dados, comparativos ou históricos da área ajudam a explicar o contexto atual desta decisão?'],
    spinProblema: ['Em que ponto os números ou a execução mostram que o modelo atual já não está entregando o retorno esperado?'],
    spinImplicacao: ['Se essa diferença aparecer em sacas por hectare, margem e custo operacional, quanto isso representa na conta da fazenda?'],
    spinNecessidade: ['Que evidência técnica precisa ficar clara para a decisão avançar com segurança?'],
    epa: ['Reforce credibilidade técnica, comparativo justo e lógica econômica antes de tensionar o fechamento.'],
    sine: ['Conduza a narrativa com dados, comparações e critério objetivo de decisão.']
  },
  diretivo: {
    titulo: 'Abordagem objetiva, curta e focada em resultado',
    linguagem: [
      'Use frases curtas, diretas e sem rodeios.',
      'Fale de resultado, produtividade, rentabilidade e tempo ganho.'
    ],
    postura: [
      'Vá direto ao ponto e evite alongar a conversa em explicações laterais.',
      'Mostre rapidamente o que ganha, o que perde e qual decisão precisa ser tomada.'
    ],
    ritmo: ['Mantenha a conversa rápida e pragmática.', 'Priorize os pontos que movem decisão e corte o resto.'],
    fechamento: ['Feche com um compromisso simples, claro, rápido e com dono definido.'],
    opc: [
      'Conduza a conversa para decisão prática e impacto visível no ciclo.',
      'Evite excesso de contexto e leve cedo para ganho financeiro ou operacional.'
    ],
    spinSituacao: ['Hoje, objetivamente, qual critério define essa decisão?'],
    spinProblema: ['Onde isso está travando resultado ou fazendo perder dinheiro?'],
    spinImplicacao: ['Se nada mudar agora, qual perda entra primeiro na conta?'],
    spinNecessidade: ['O que precisa ficar claro para decidir isso hoje?'],
    epa: ['Seja firme, direto e focado no impacto no resultado, sem excesso de explicação.'],
    sine: ['Dê ritmo rápido à narrativa e leve cada etapa para uma decisão prática.']
  },
  integrador: {
    titulo: 'Abordagem empática, segura e construída em parceria',
    linguagem: [
      'Use linguagem acolhedora, simples e respeitosa.',
      'Evite pressão e mostre disposição real de construir a decisão junto.'
    ],
    postura: [
      'Priorize confiança, segurança e escuta ativa.',
      'Mostre que o produtor terá apoio durante a validação e depois dela.'
    ],
    ritmo: ['Conduza no tempo do produtor, sem apressar conclusões.', 'Valide entendimento com frequência para manter conforto e adesão.'],
    fechamento: ['Feche com um compromisso construído em conjunto, com segurança e acompanhamento próximos.'],
    opc: [
      'Construa o compromisso junto com o produtor, respeitando o tempo dele para amadurecer a decisão.',
      'Mostre suporte e constância, não apenas recomendação pontual.'
    ],
    spinSituacao: ['Como o senhor tem vivido esse cenário na prática e o que hoje te deixa mais seguro ou mais preocupado nessa decisão?'],
    spinProblema: ['Onde esse ponto vem incomodando mais no dia a dia da fazenda?'],
    spinImplicacao: ['Se isso seguir assim, o que pode pesar mais para a lavoura e para a tranquilidade da operação?'],
    spinNecessidade: ['Que formato de apoio te deixaria mais confortável para avançar com segurança?'],
    epa: ['Personalize com acolhimento, segurança e confiança antes de pressionar por avanço.'],
    sine: ['Faça a narrativa parecer uma construção conjunta, não um discurso fechado.']
  },
  expressivo: {
    titulo: 'Abordagem envolvente, inspiradora e orientada a futuro',
    linguagem: [
      'Use energia, visão de futuro e linguagem envolvente.',
      'Valorize conquista, diferenciação e oportunidade de avanço.'
    ],
    postura: [
      'Conecte a solução a imagem, protagonismo e resultado percebido.',
      'Evite excesso de detalhe técnico sem contexto inspirador.'
    ],
    ritmo: ['Mantenha a conversa viva e provocativa.', 'Use exemplos e imagens de futuro para criar movimento.'],
    fechamento: ['Feche com entusiasmo, visão de ganho e um próximo passo que gere tração rápida.'],
    opc: [
      'Leve a conversa para oportunidade, protagonismo e resultado que o produtor quer enxergar na fazenda.',
      'Dê energia ao fechamento sem perder objetividade agronômica.'
    ],
    spinSituacao: ['Quando você olha para essa área, o que te anima e o que te incomoda mais hoje no potencial que ainda não virou resultado?'],
    spinProblema: ['Onde a fazenda está ficando aquém do que poderia entregar se esse ponto não for corrigido?'],
    spinImplicacao: ['Se esse gargalo continuar, quanto de resultado e oportunidade ficam para trás na próxima safra?'],
    spinNecessidade: ['Que tipo de avanço faria sentido para você enxergar valor logo e ganhar confiança para dar o próximo passo?'],
    epa: ['Conecte o valor ao futuro desejado, à conquista visível e ao ganho percebido.'],
    sine: ['Dê à narrativa energia, visão de futuro e sensação de movimento.']
  }
};

const THEME_PATTERNS: Array<{ theme: Exclude<Theme, 'geral'>; regex: RegExp }> = [
  { theme: 'adubo', regex: /adubo|adubacao|fertilizante|npk|ureia|map|kcl|fosfato|potassio|nitrogenio/ },
  {
    theme: 'defensivo',
    regex: /defensivo|fungicida|herbicida|inseticida|manejo de pragas|pragas|daninhas|doencas|cigarrinha|percevejo|lagarta|tripes|acaro|bicudo/
  },
  { theme: 'semente', regex: /semente|hibrido|cultivar|variedade|germinacao|vigor/ },
  { theme: 'biologico', regex: /biologico|bioinsumo|inoculante|trichoderma|bacillus/ },
  { theme: 'irrigacao', regex: /irrigacao|lamina|pivo|gotejo|turno de rega/ },
  { theme: 'maquinario', regex: /maquina|maquinario|pulverizador|plantadeira|colheitadeira|calibracao/ },
  { theme: 'servicos', regex: /servico|assistencia|consultoria|suporte tecnico|pos venda/ }
];

const SPECIFIC_TERM_PATTERNS: Array<{ label: string; regex: RegExp }> = [
  { label: 'NPK', regex: /\bnpk\b/ },
  { label: 'Ureia', regex: /\bureia\b/ },
  { label: 'MAP', regex: /\bmap\b/ },
  { label: 'KCl', regex: /\bkcl\b|cloreto de potassio/ },
  { label: 'Foliar', regex: /foliar/ },
  { label: 'Bioinsumo', regex: /bioinsumo|biologico|inoculante/ },
  { label: 'Fungicida', regex: /fungicida/ },
  { label: 'Herbicida', regex: /herbicida/ },
  { label: 'Inseticida', regex: /inseticida/ },
  { label: 'Cigarrinha', regex: /cigarrinha/ },
  { label: 'Percevejo', regex: /percevejo/ },
  { label: 'Lagarta', regex: /lagarta/ },
  { label: 'Híbrido', regex: /hibrido|cultivar|variedade/ },
  { label: 'Taxa variável', regex: /taxa variavel|vra/ },
  { label: 'Piloto automático', regex: /piloto automatico|autosteer/ },
  { label: 'Telemetria', regex: /telemetria/ },
  { label: 'ISOBUS', regex: /isobus/ },
  { label: 'Corte de seção', regex: /corte de secao|section control/ },
  { label: 'Mapa de aplicação', regex: /mapa de aplicacao|mapa de prescricao/ },
  { label: 'RTK', regex: /\brtk\b/ },
  { label: 'Monitor de bordo', regex: /monitor de bordo|display/ },
  { label: 'Plantadeira', regex: /plantadeira/ },
  { label: 'Pulverizador', regex: /pulverizador/ },
  { label: 'Colheitadeira', regex: /colheitadeira/ }
];

const MACHINE_TECH_LABELS = new Set([
  'Taxa variável',
  'Piloto automático',
  'Telemetria',
  'ISOBUS',
  'Corte de seção',
  'Mapa de aplicação',
  'RTK',
  'Monitor de bordo'
]);

const determineCulture = (tags: string[]): ScenarioAnalysis['cultura'] => {
  if (tags.includes('soja')) return 'soja';
  if (tags.includes('milho')) return 'milho';
  if (tags.includes('cana')) return 'cana';
  if (tags.includes('cafe')) return 'cafe';
  if (tags.includes('pecuaria')) return 'pecuaria';
  return 'geral';
};

const determineStage = (tags: string[]): ScenarioAnalysis['etapa'] => {
  if (tags.includes('primeira_visita')) return 'primeiro_contato';
  if (tags.includes('upsell') || tags.includes('cross_sell')) return 'expansao';
  if (tags.includes('orientado_preco') || tags.includes('cenario_competitivo')) return 'negociacao';
  if (tags.includes('revisao_relacionamento')) return 'relacionamento';
  if (tags.includes('pos_venda')) return 'pos_venda';
  return 'negociacao';
};

const detectThemes = (normalized: string): Theme[] => {
  const found = unique(THEME_PATTERNS.filter((item) => item.regex.test(normalized)).map((item) => item.theme));
  return found.length ? [...found] : ['geral'];
};

const inferPrimaryTheme = (normalized: string, themes: Theme[]): Theme => {
  if (/inseticida|cigarrinha|percevejo|lagarta|tripes|acaro|bicudo|controle de pragas/.test(normalized)) {
    return 'defensivo';
  }

  if (/taxa variavel|telemetria|piloto automatico|isobus|pulverizador|plantadeira|colheitadeira/.test(normalized)) {
    return 'maquinario';
  }

  if (/adubo|fertilizante|npk|ureia|map|kcl/.test(normalized)) {
    return 'adubo';
  }

  if (/semente|hibrido|cultivar|variedade/.test(normalized)) {
    return 'semente';
  }

  if (/biologico|bioinsumo|inoculante|trichoderma|bacillus/.test(normalized)) {
    return 'biologico';
  }

  if (/irrigacao|lamina|pivo|gotejo/.test(normalized)) {
    return 'irrigacao';
  }

  return themes[0] ?? 'geral';
};

const detectSpecificTerms = (normalized: string): string[] =>
  unique(SPECIFIC_TERM_PATTERNS.filter((item) => item.regex.test(normalized)).map((item) => item.label));

const describeThemeFocus = (theme: Theme): string => {
  const map: Record<Theme, string> = {
    adubo: 'adubação e eficiência nutricional',
    defensivo: 'manejo fitossanitário e controle de risco',
    semente: 'escolha de genética, vigor e estabelecimento',
    biologico: 'uso de biológicos e consistência de manejo',
    irrigacao: 'estratégia de irrigação e estabilidade produtiva',
    maquinario: 'execução operacional e qualidade de aplicação',
    servicos: 'serviço técnico e suporte em campo',
    geral: 'decisão técnico-econômica da operação'
  };
  return map[theme];
};

const resolveConversationTheme = (analysis: ScenarioAnalysis, productFocus?: ProductIntelligence): Theme =>
  (productFocus?.temas.find((tema) => tema !== 'geral') as Theme | undefined) ?? analysis.primaryTheme;

const hasWholeTokenMatch = (normalized: string, alias: RegExp): boolean => {
  const flags = alias.flags.includes('g') ? alias.flags : `${alias.flags}g`;
  const matcher = new RegExp(alias.source, flags);
  let match = matcher.exec(normalized);

  while (match) {
    const start = match.index ?? 0;
    const end = start + match[0].length;
    const before = start === 0 ? ' ' : normalized[start - 1];
    const after = end >= normalized.length ? ' ' : normalized[end];
    const beforeOk = !/[a-z0-9]/.test(before);
    const afterOk = !/[a-z0-9]/.test(after);

    if (beforeOk && afterOk) {
      return true;
    }

    match = matcher.exec(normalized);
  }

  return false;
};

const describeCompetitiveDifferentiation = (analysis: ScenarioAnalysis): string => {
  if (analysis.primaryTheme === 'maquinario') {
    return analysis.machineTechTerms.length
      ? `Diferenciar por captura de valor da tecnologia embarcada (${analysis.machineTechTerms.slice(0, 4).join(', ')}) em vez de comparar só potência ou preço.`
      : 'Diferenciar por eficiência operacional e uso de tecnologia embarcada, em vez de comparar apenas preço de máquina/implemento.';
  }

  if (analysis.primaryTheme === 'adubo') {
    return 'Diferenciar por retorno por hectare, eficiência nutricional e previsibilidade de safra, não por preço por tonelada.';
  }

  if (analysis.primaryTheme === 'defensivo') {
    return 'Diferenciar por redução de risco, estabilidade de controle e custo total do manejo, não por preço do litro/quilo.';
  }

  if (analysis.specificTerms.length) {
    return `Diferenciar conectando os termos citados (${analysis.specificTerms.slice(0, 4).join(', ')}) ao impacto econômico no resultado final.`;
  }

  return 'Diferenciar por impacto em margem, produtividade e previsibilidade, evitando guerra de preços.';
};

const describeProductDifferentiation = (product?: ProductIntelligence): string | null => {
  if (!product) return null;
  return `${product.produto} (${product.fabricante}): defender por resultado no talhão, risco evitado e impacto econômico no ciclo, não por preço isolado.`;
};

const selectAgroChallenges = (analysis: ScenarioAnalysis): AgroChallengeSignal[] => {
  const scored = AGRO_CHALLENGE_SIGNALS.map((item) => {
    let score = 0;

    if (item.temas.includes('geral')) score += 1;
    if (item.temas.includes(analysis.primaryTheme)) score += 3;
    if (analysis.primaryTheme === 'maquinario' && item.id === 'tecnologia-embarcada') score += 4;
    if ((analysis.foco === 'preco' || analysis.prioridade === 'competicao') && item.id === 'credito-capital') score += 2;
    if (analysis.tags.includes('cenario_competitivo') && item.id === 'produtividade-margem') score += 1;

    return { item, score };
  })
    .sort((a, b) => b.score - a.score)
    .map((entry) => entry.item);

  return unique(scored.map((item) => item.id))
    .map((id) => scored.find((item) => item.id === id))
    .filter((item): item is AgroChallengeSignal => Boolean(item))
    .slice(0, 3);
};

const buildMissingScenarioDetails = (analysis: ScenarioAnalysis, productMentioned = false): string[] => {
  const hints: string[] = [];
  const text = analysis.normalized;

  if (analysis.specificTerms.length === 0 && !productMentioned) {
    hints.push('Cite o produto/solução em discussão e o resultado esperado com ele (ex.: proteger margem, ganhar produtividade, reduzir risco).');
  }

  if (productMentioned) {
    hints.push('Você já citou o produto. Para melhorar a qualidade da preparação, acrescente cultura, área (ha), fase da safra e objetivo esperado com essa decisão.');
  }

  if (!/margem|roi|retorno|produtividade|custo|eficiencia|risco|previsibilidade/.test(text)) {
    hints.push('Inclua 1 indicador de negócio prioritário (margem, produtividade, custo/ha, risco operacional ou previsibilidade).');
  }

  if (!/prazo|janela|safra|plantio|colheita|dias|semana|mes/.test(text)) {
    hints.push('Inclua momento da safra e prazo crítico de decisão (ex.: definir em 7 dias antes da janela de plantio).');
  }

  if (!/stakeholder|compras|financeiro|agronomo|socio|familia|gerente|diretor/.test(text)) {
    hints.push('Inclua quem decide (produtor, agrônomo, financeiro, compras) e quem pode travar o avanço.');
  }

  if (analysis.primaryTheme === 'maquinario' && analysis.machineTechTerms.length === 0) {
    hints.push('Em máquinas/implementos, inclua tecnologias embarcadas já usadas ou negligenciadas (telemetria, taxa variável, piloto, ISOBUS).');
  }

  return hints.slice(0, 4);
};

const buildThemeSpinInjections = (theme: Theme): {
  situacao: string[];
  problema: string[];
  implicacao: string[];
  necessidadeSolucao: string[];
} => {
  if (theme === 'adubo') {
    return {
      situacao: ['Hoje, quais adubos/fertilizantes vocês mais utilizam e qual lógica técnica/econômica sustenta essa escolha?'],
      problema: ['Em quais talhões a estratégia atual de adubação tem mostrado menor resposta produtiva ou maior pressão de custo por hectare?'],
      implicacao: ['Quando a adubação é definida só por preço de produto, quanto isso pode custar em produtividade, eficiência e margem no fim da safra?'],
      necessidadeSolucao: ['Que evidência técnica e econômica você precisa ver para ajustar a adubação com foco em retorno por hectare e não em preço por tonelada?']
    };
  }

  if (theme === 'defensivo') {
    return {
      situacao: ['Quais defensivos e estratégias de rotação vocês utilizam hoje e em quais momentos do ciclo a decisão é mais crítica?'],
      problema: ['Onde o manejo atual tem gerado maior pressão de resistência, retrabalho ou perda de controle?'],
      implicacao: ['Se esse padrão continuar, qual impacto esperado em custo total de controle e perda de produtividade?'],
      necessidadeSolucao: ['Qual combinação de evidência de campo e impacto econômico te dá segurança para evoluir o manejo?']
    };
  }

  if (theme === 'semente') {
    return {
      situacao: ['Quais cultivares/híbridos vocês adotam hoje e quais critérios pesam mais na escolha?'],
      problema: ['Em que áreas o material atual tem limitado teto produtivo, estabilidade ou qualidade do stand?'],
      implicacao: ['Qual custo de oportunidade existe ao manter genética que não acompanha a meta de resultado da operação?'],
      necessidadeSolucao: ['Que validação técnica e econômica você precisa para avançar na escolha de material com maior retorno?']
    };
  }

  if (theme === 'biologico') {
    return {
      situacao: ['Quais biológicos vocês já usam hoje e com qual protocolo de aplicação e acompanhamento?'],
      problema: ['Onde a adoção atual de biológicos ainda gera insegurança de consistência de resultado?'],
      implicacao: ['Qual impacto para a operação quando o biológico é usado sem critério claro de posicionamento e validação?'],
      necessidadeSolucao: ['Que desenho de validação em campo te daria confiança para ampliar adoção com segurança econômica?']
    };
  }

  if (theme === 'irrigacao') {
    return {
      situacao: ['Como vocês definem hoje lâmina, frequência e prioridade de área na irrigação?'],
      problema: ['Onde o manejo atual de irrigação tem gerado maior variabilidade de produtividade ou custo de energia?'],
      implicacao: ['Se esse padrão seguir, qual risco de perda de eficiência hídrica, energia e resultado final?'],
      necessidadeSolucao: ['Que indicadores de eficiência e retorno precisam melhorar para justificar a mudança de estratégia?']
    };
  }

  if (theme === 'maquinario') {
    return {
      situacao: ['Quais equipamentos são críticos hoje e como vocês controlam calibração e qualidade de execução?'],
      problema: ['Onde a operação tem mais retrabalho, variação de aplicação ou perda por execução?'],
      implicacao: ['Qual impacto de execução abaixo do padrão em custo operacional e resultado da safra?'],
      necessidadeSolucao: ['Que melhoria operacional precisa ser comprovada para justificar o investimento em ajuste/tecnologia?']
    };
  }

  if (theme === 'servicos') {
    return {
      situacao: ['Quais serviços técnicos vocês usam hoje e quais critérios definem qualidade de atendimento em campo?'],
      problema: ['Onde o suporte atual ainda não consegue transformar orientação técnica em resultado econômico?'],
      implicacao: ['Qual custo de decisão tardia ou execução sem acompanhamento adequado para a operação?'],
      necessidadeSolucao: ['Que modelo de serviço te daria mais previsibilidade e confiança para avançar?']
    };
  }

  return {
    situacao: [],
    problema: [],
    implicacao: [],
    necessidadeSolucao: []
  };
};

const buildSpecificTermInjections = (analysis: ScenarioAnalysis, theme: Theme): {
  situacao: string[];
  problema: string[];
  implicacao: string[];
  necessidadeSolucao: string[];
} => {
  const terms = analysis.specificTerms.slice(0, 4).join(', ');
  if (!terms) {
    return { situacao: [], problema: [], implicacao: [], necessidadeSolucao: [] };
  }

  const base = {
    situacao: [`Hoje, como vocês estão decidindo tecnicamente o uso de ${terms} e como medem retorno econômico dessas escolhas?`],
    problema: [`Em quais situações o uso atual de ${terms} não tem convertido em resultado na proporção esperada?`],
    implicacao: [`Quando ${terms} são escolhidos sem critério de retorno total, qual impacto isso gera em margem e previsibilidade?`],
    necessidadeSolucao: [`Que evidência vocês precisam para ajustar o uso de ${terms} com foco em resultado por hectare e segurança operacional?`]
  };

  if (theme === 'maquinario') {
    const machineAsset = analysis.specificTerms.find((term) =>
      ['Plantadeira', 'Pulverizador', 'Colheitadeira'].includes(term)
    );
    const assetText = machineAsset ? `na ${machineAsset.toLowerCase()}` : 'nos equipamentos';
    const techText = analysis.machineTechTerms.length
      ? analysis.machineTechTerms.join(', ')
      : 'piloto automático, taxa variável, telemetria e corte de seção';

    return {
      situacao: [
        `Quais tecnologias embarcadas vocês usam hoje ${assetText} (${techText}) e quais estão subutilizadas na operação?`
      ],
      problema: [
        `Onde a baixa utilização de tecnologia embarcada ${assetText} tem gerado desperdício, sobreposição ou falha de execução?`
      ],
      implicacao: [
        `Qual custo operacional e de produtividade aparece quando recursos de ${techText} não entram no padrão de trabalho da fazenda?`
      ],
      necessidadeSolucao: [
        `Que plano de adoção e treinamento faria vocês capturarem valor real da tecnologia embarcada ${assetText} já nesta safra?`
      ]
    };
  }

  return base;
};

const analyzeScenario = (scenario: string): ScenarioAnalysis => {
  const normalized = normalizeText(scenario);
  const tags = unique(
    TAG_PATTERNS.filter((pattern) => pattern.regex.test(normalized)).map((pattern) => pattern.tag)
  );

  const cultura = determineCulture(tags);
  const etapa = determineStage(tags);
  const themes = detectThemes(normalized);
  const primaryTheme = inferPrimaryTheme(normalized, themes);
  const specificTerms = detectSpecificTerms(normalized);
  const machineTechTerms = specificTerms.filter((term) => MACHINE_TECH_LABELS.has(term));
  const foco: ScenarioAnalysis['foco'] = tags.includes('orientado_preco') ? 'preco' : tags.length ? 'misto' : 'valor';
  const stakeholders: ScenarioAnalysis['stakeholders'] = tags.includes('stakeholders_multiplos') ? 'multiplos' : 'simples';
  const fabricantes = FABRICANTE_REFERENCIAS.filter((item) => item.regex.test(normalized)).map((item) => item.fabricante);

  const priorityRule = PRIORITY_BY_TAG.find((rule) =>
    rule.when({
      normalized,
      tags,
      themes,
      primaryTheme,
      specificTerms,
      machineTechTerms,
      cultura,
      etapa,
      foco,
      stakeholders,
      fabricantes,
      prioridade: DEFAULT_PRIORITY
    })
  );

  return {
    normalized,
    tags,
    themes,
    primaryTheme,
    specificTerms,
    machineTechTerms,
    cultura,
    etapa,
    foco,
    stakeholders,
    fabricantes,
    prioridade: priorityRule?.value ?? DEFAULT_PRIORITY
  };
};

const detectProductIntelligence = (
  normalized: string,
  mentionedManufacturers: string[] = []
): ProductIntelligence[] => {
  const scored = PRODUCT_INTELLIGENCE_DB.map((item) => {
    const aliasMatches = item.aliases.filter((alias) => hasWholeTokenMatch(normalized, alias)).length;
    if (!aliasMatches) {
      return { item, score: -1 };
    }

    const productName = normalizeText(item.produto);
    const productNamePattern = new RegExp(`\\b${productName.replace(/\s+/g, '\\s+')}\\b`);
    const exactProductName = productNamePattern.test(normalized);
    const manufacturerBoost = mentionedManufacturers.includes(item.fabricante) ? 2 : 0;
    const score = aliasMatches + (exactProductName ? 6 : 0) + manufacturerBoost + Math.min(productName.length / 12, 2);

    return { item, score };
  })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((entry) => entry.item);

  return unique(scored.map((item) => `${item.fabricante}::${normalizeText(item.produto)}`))
    .map((id) => scored.find((item) => `${item.fabricante}::${normalizeText(item.produto)}` === id))
    .filter((item): item is ProductIntelligence => Boolean(item));
};

const detectPotentialProductTerms = (normalized: string): string[] => {
  const stopWords =
    /reuniao|interacao|produtor|cliente|safra|preco|valor|negocio|agro|venda|vendas|campo|cenario|preparacao|estrategica|comercial|tecnologia|tecnologias|produto|produtos|solucao|solucoes|manejo|operacao|plantio|colheita|talhao|area|custo|margem|risco|previsibilidade|resultado|decisao|avanco|objetivo|processo|compromisso|spin|epa|challenger|opc|premium|agregado|caro|caros|linha|linhas|portfolio|portifolio/;
  const tokens = unique(
    normalized
      .split(/\s+/)
      .filter((token) => token.length >= 5 && /^[a-z0-9-]+$/.test(token))
      .filter((token) => !stopWords.test(token))
  );

  return tokens.slice(0, 6);
};

const extractExplicitTechByManufacturer = (
  normalizedScenario: string,
  fabricantes: string[]
): Array<{ fabricante: string; tecnologia: string }> => {
  const invalidAsProduct = new Set([
    'produto',
    'produtos',
    'solucao',
    'solucoes',
    'tecnologia',
    'tecnologias',
    'premium',
    'agregado',
    'valor',
    'caro',
    'caros',
    'linha',
    'linhas',
    'portfolio',
    'portifolio',
    'manejo',
    'sistema',
    'praga',
    'pragas',
    'doenca',
    'doencas',
    'daninha',
    'daninhas',
    'inseto',
    'insetos',
    'cigarrinha',
    'percevejo',
    'lagarta',
    'tripes',
    'acaro',
    'bicudo',
    'ferrugem',
    'cercospora',
    'soja',
    'milho',
    'cana',
    'cafe',
    'algodao',
    'citrus',
    'hortifruti',
    'vegetais',
    'plantio',
    'colheita',
    'safra',
    'talhao',
    'area',
    'deficiencia',
    'deficiencias',
    'insuficiencia',
    'carencia',
    'carencias',
    'estresse',
    'hidrico',
    'seca',
    'calor',
    'lixiviacao',
    'volatilizacao',
    'fixacao',
    'compactacao',
    'desuniformidade',
    'variabilidade',
    'instabilidade',
    'uniformidade',
    'vigor',
    'arranque',
    'enraizamento',
    'raiz',
    'raizes',
    'stand',
    'florada',
    'floracao',
    'pegamento',
    'enchimento',
    'metabolismo',
    'fisiologia',
    'qualidade',
    'sanidade',
    'produtividade',
    'eficiencia',
    'estabilidade',
    'resiliencia',
    'resposta',
    'respostas',
    'retorno',
    'rentabilidade',
    'margem',
    'nutricao',
    'nutricional',
    'nutricionais',
    'fertilidade',
    'solo',
    'solos',
    'adubacao',
    'fertilizante',
    'fertilizantes',
    'adubo',
    'adubos',
    'npk',
    'nitrogenio',
    'fosforo',
    'potassio',
    'nitrato',
    'nitratos',
    'micronutriente',
    'micronutrientes',
    'macronutriente',
    'macronutrientes',
    'foliar',
    'barter'
    ,
    'semente',
    'sementes',
    'hibrido',
    'hibridos',
    'cultivar',
    'cultivares',
    'variedade',
    'variedades',
    'genetica',
    'biotecnologia',
    'stand',
    'germinacao',
    'tratores',
    'trator',
    'maquina',
    'maquinas',
    'maquinario',
    'implemento',
    'implementos',
    'pulverizador',
    'pulverizadores',
    'plantadeira',
    'plantadeiras',
    'colheitadeira',
    'colheitadeiras',
    'tecnologia-embarcada',
    'telemetria',
    'piloto',
    'automatico',
    'autosteer',
    'isobus',
    'rtk',
    'taxa-variavel',
    'calibracao'
    ,
    'calcario',
    'calagem',
    'gesso',
    'gessagem',
    'gesso-agricola',
    'fosfatagem',
    'potassagem',
    'dap',
    'map',
    'ureia',
    'superfosfato',
    'cloreto',
    'cloreto-de-potassio',
    'organico',
    'organicos',
    'esterco',
    'composto',
    'compostos',
    'compostagem',
    'micronutriente',
    'micronutrientes',
    'macronutriente',
    'macronutrientes',
    'boro',
    'zinco',
    'cobre',
    'manganes',
    'ferro',
    'molibdenio',
    'enxofre',
    'calcio',
    'magnesio',
    'herbicida',
    'herbicidas',
    'inseticida',
    'inseticidas',
    'fungicida',
    'fungicidas',
    'acaricida',
    'acaricidas',
    'fitossanitario',
    'fitossanitarios',
    'bioinsumo',
    'bioinsumos',
    'biologico',
    'biologicos',
    'glifosato',
    'atrazina',
    'acefato',
    'clorpirifos',
    'mancozebe',
    'clorotalonil',
    'semente-certificada',
    'certificada',
    'certificadas',
    'muda',
    'mudas',
    'germinativo',
    'germinacao',
    'combustivel',
    'combustiveis',
    'lubrificante',
    'lubrificantes',
    'semeadora',
    'semeadoras',
    'colhedora',
    'colhedoras',
    'equipamento',
    'equipamentos',
    'pivo',
    'pivos',
    'tubulacao',
    'tubulacoes',
    'irrigacao',
    'agua',
    'adjuvante',
    'adjuvantes',
    'surfactante',
    'espalhante',
    'adesivo',
    'oleo-mineral',
    'oleo-vegetal',
    'coadjuvante',
    'coadjuvantes'
  ]);
  const genericDescriptors = new Set([
    'produto',
    'produtos',
    'solucao',
    'solucoes',
    'tecnologia',
    'tecnologias',
    'premium',
    'agregado',
    'valor',
    'caro',
    'caros',
    'linha',
    'linhas',
    'portfolio',
    'portifolio',
    'manejo',
    'sistema'
  ]);
  const found: Array<{ fabricante: string; tecnologia: string }> = [];

  fabricantes.forEach((fabricante) => {
    const key = normalizeText(fabricante).split(' ')[0];
    const regex = new RegExp(`\\b([a-z0-9-]{4,})\\s+(?:da|do)\\s+${key}\\b`, 'g');
    let match = regex.exec(normalizedScenario);
    while (match) {
      const token = match[1];
      if (!genericDescriptors.has(token) && !invalidAsProduct.has(token)) {
        found.push({ fabricante, tecnologia: token });
      }
      match = regex.exec(normalizedScenario);
    }
  });

  return found;
};

const buildDynamicProductProfiles = (
  analysis: ScenarioAnalysis,
  normalizedScenario: string,
  staticProducts: ProductIntelligence[]
): ProductIntelligence[] => {
  if (!analysis.fabricantes.length) {
    return [];
  }

  const knownNames = new Set(staticProducts.map((item) => normalizeText(item.produto)));
  const explicit = extractExplicitTechByManufacturer(normalizedScenario, analysis.fabricantes);
  const dynamicRaw = explicit;

  const uniqueDynamic = dynamicRaw.filter((item, idx, arr) => {
    const key = `${item.fabricante}::${item.tecnologia}`;
    return arr.findIndex((x) => `${x.fabricante}::${x.tecnologia}` === key) === idx;
  }).slice(0, 4);

  return uniqueDynamic
    .filter((item) => !knownNames.has(normalizeText(item.tecnologia)))
    .map((item) => {
      const ref = FABRICANTE_REFERENCIAS.find((x) => x.fabricante === item.fabricante);
      const tech = item.tecnologia.toUpperCase();
      const themeFocus = describeThemeFocus(analysis.primaryTheme);
      const inferredFromContext = inferPrimaryTheme(normalizedScenario, [analysis.primaryTheme]);
      const primaryTheme = inferredFromContext === 'geral' ? 'servicos' : inferredFromContext;

      return {
        produto: tech,
        fabricante: item.fabricante,
        aliases: [new RegExp(`\\b${normalizeText(tech)}\\b`)],
        temas: [primaryTheme, 'geral'],
        propostaValorCurta:
          `${tech} com foco em resultado de negócio no campo, conectando ${themeFocus} com margem, produtividade e previsibilidade.`,
        fortalezasCompetitivas: [
          `${tech} deve ser defendido pelo impacto no resultado final da safra, e não por comparação isolada de preço.`,
          'A proposta ganha força quando conecta execução em campo, risco evitado e retorno econômico por área.',
          `Use fontes oficiais da ${item.fabricante} para sustentar posicionamento técnico e comercial com credibilidade.`
        ],
        cuidadosDePosicionamento: [
          'Sempre alinhar recomendação ao contexto da fazenda: cultura, fase da safra, equipe e capacidade de execução.',
          'Definir critério simples de validação em área piloto para sustentar decisão de escala com segurança.',
          'Evitar promessa genérica: traduzir benefício técnico em número de negócio que o produtor reconhece.'
        ],
        ganchosSpin: {
          situacao: [
            `Como vocês têm usado ${tech} até aqui e quais critérios definem quando essa tecnologia entra no manejo?`
          ],
          problema: [
            `Onde o uso atual de ${tech} ainda não está entregando o resultado esperado no campo?`
          ],
          implicacao: [
            `Se esse padrão seguir, qual impacto isso traz em margem, risco operacional e previsibilidade da safra?`
          ],
          necessidadeSolucao: [
            `Que condição de resultado precisa ficar clara para você concordar em avançar com ${tech} no próximo passo?`
          ]
        },
        fontes: ref?.fontes?.slice(0, 2) ?? [],
        atualizadoEm: new Date().toISOString().slice(0, 10)
      };
    });
};

const canonical = (line: string): string =>
  normalizeText(line)
    .replace(/[.,;:!?()]/g, '')
    .split(' ')
    .filter((word) => word.length > 3)
    .slice(0, 8)
    .join(' ');

const composeUnique = (lines: string[], amount: number): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const line of lines) {
    const cleanLine = line.trim();
    if (!cleanLine) {
      continue;
    }

    const key = canonical(cleanLine);
    if (!seen.has(key)) {
      seen.add(key);
      result.push(cleanLine);
    }

    if (result.length >= amount) {
      break;
    }
  }

  return result;
};

const metricByCulture = (culture: ScenarioAnalysis['cultura']): string => {
  if (culture === 'soja' || culture === 'milho') return 'R$/ha, estabilidade de produtividade e eficiência de janela operacional';
  if (culture === 'cana') return 'TCH/ATR, custo por tonelada e impacto de longevidade do canavial';
  if (culture === 'cafe') return 'produtividade, padrão de qualidade e previsibilidade de receita';
  if (culture === 'pecuaria') return 'arroba por área, giro, custo de nutrição e eficiência operacional';
  return 'margem, previsibilidade e custo total da operação';
};

const buildManufacturerNotes = (analysis: ScenarioAnalysis): string[] => {
  if (!analysis.fabricantes.length) {
    return [];
  }

  return analysis.fabricantes
    .map((fabricante) => FABRICANTE_REFERENCIAS.find((item) => item.fabricante === fabricante))
    .filter((item): item is NonNullable<typeof item> => Boolean(item))
    .map(
      (item) =>
        `Se ${item.fabricante} aparecer como referência do cliente, conduza comparação por critério de negócio: ${item.focoConversa}`
    );
};

const buildManufacturerReferences = (analysis: ScenarioAnalysis): Array<{ fabricante: string; fonte: string }> => {
  return analysis.fabricantes.flatMap((fabricante) => {
    const item = FABRICANTE_REFERENCIAS.find((ref) => ref.fabricante === fabricante);
    if (!item) return [];
    return item.fontes.map((fonte) => ({ fabricante: item.fabricante, fonte }));
  });
};

const buildSectorReferences = (
  analysis: ScenarioAnalysis
): Array<{ fabricante: string; focoConversa: string; fonte: string }> => {
  const byTheme = FABRICANTE_REFERENCIAS.filter(
    (item) => item.temas?.includes(analysis.primaryTheme) || item.temas?.includes('geral')
  );

  const prioritized = [
    ...byTheme.filter((item) => analysis.fabricantes.includes(item.fabricante)),
    ...byTheme.filter((item) => !analysis.fabricantes.includes(item.fabricante))
  ];

  const seen = new Set<string>();
  const selected = prioritized.filter((item) => {
    if (seen.has(item.fabricante)) return false;
    seen.add(item.fabricante);
    return true;
  }).slice(0, 8);

  return selected.flatMap((item) =>
    item.fontes.slice(0, 1).map((fonte) => ({
      fabricante: item.fabricante,
      focoConversa: item.focoConversa,
      fonte
    }))
  );
};

const emptyStrategicHooks = (): StrategicHooks => ({
  opcProcess: [],
  spinNeed: [],
  epaEducar: [],
  tips: [],
  roiHooks: []
});

const inferYaraLinesByScenario = (analysis: ScenarioAnalysis, normalizedScenario: string): string[] => {
  const base = YARA_CROP_FOCUS[analysis.cultura] ?? YARA_CROP_FOCUS.geral;
  const matchedProblems = YARA_PROBLEM_TO_LINES.filter((item) => item.regex.test(normalizedScenario)).flatMap((item) => item.lines);
  const matchedStages = YARA_STAGE_TO_LINES.filter((item) => item.regex.test(normalizedScenario)).flatMap((item) => item.lines);
  const matchedObjectives = YARA_OBJECTIVE_TO_LINES.filter((item) => item.regex.test(normalizedScenario)).flatMap((item) => item.lines);

  return unique([...base, ...matchedProblems, ...matchedStages, ...matchedObjectives]).slice(0, 5);
};

const buildStrategicHooksByPlayer = (
  analysis: ScenarioAnalysis,
  normalizedScenario: string,
  productFocus?: ProductIntelligence
): StrategicHooks => {
  const primaryPlayer = productFocus?.fabricante ?? analysis.fabricantes[0] ?? 'C.Vale';
  if (!primaryPlayer) {
    return emptyStrategicHooks();
  }

  const playbook = PLAYER_VALUE_PLAYBOOKS.find((item) => item.fabricante === primaryPlayer);
  if (!playbook) {
    return emptyStrategicHooks();
  }

  const productName = productFocus?.fabricante === primaryPlayer ? productFocus.produto : `portfólio ${primaryPlayer}`;
  const linhaResumo = playbook.linhas.map((item) => `${item.linha} (${item.papel})`).slice(0, 3).join(' | ');
  const globalRuleText = `Diretriz de valor: priorize ${VALUE_SELLING_GLOBAL_RULES.priority.slice(0, 3).join(', ')} e evite ${VALUE_SELLING_GLOBAL_RULES.avoid.slice(0, 2).join(', ')}.`;
  const yaraSuggestedLines =
    primaryPlayer === 'Yara' ? inferYaraLinesByScenario(analysis, normalizedScenario) : [];
  const cortevaHooks =
    primaryPlayer === 'Corteva' ? buildCortevaValueHooks(normalizedScenario, analysis.cultura) : null;
  const yaraLineDetail =
    primaryPlayer === 'Yara' && yaraSuggestedLines.length
      ? `Linhas recomendadas para este cenário: ${yaraSuggestedLines
          .map((line) => `${line} (${YARA_LINE_ROLE_MAP[line as keyof typeof YARA_LINE_ROLE_MAP]?.role ?? 'valor'})`)
          .join(' | ')}.`
      : '';

  return {
    opcProcess: composeUnique(
      [
        `No contexto ${primaryPlayer}, conduza a conversa por valor de sistema e resultado econômico no talhão.`,
        `Linha estratégica para apoiar o raciocínio: ${linhaResumo}.`,
        ...(cortevaHooks ? [cortevaHooks.systemRecommended, ...cortevaHooks.opcProcess] : []),
        yaraLineDetail,
        globalRuleText,
        ...playbook.hooks.opcProcess
      ],
      4
    ),
    spinNeed: composeUnique(
      [
        `No caso de ${productName}, qual condição de resultado você precisa ver para defender internamente esta decisão?`,
        ...(cortevaHooks?.spinNeed ?? []),
        ...(primaryPlayer === 'Yara' && yaraSuggestedLines.length
          ? [`Considerando ${yaraSuggestedLines.slice(0, 3).join(', ')}, qual critério de decisão vamos acordar hoje para avançar com segurança?`]
          : []),
        ...playbook.hooks.spinNeed
      ],
      4
    ),
    epaEducar: composeUnique(
      [
        ...(cortevaHooks?.educar ?? []),
        yaraLineDetail,
        ...playbook.hooks.epaEducar
      ],
      4
    ),
    tips: composeUnique(
      [
        `Use a trilha ${primaryPlayer} como roteiro de valor: problema de campo -> decisão técnica -> impacto econômico.`,
        ...(cortevaHooks ? [cortevaHooks.systemRecommended] : []),
        ...playbook.hooks.tips
      ],
      3
    ),
    roiHooks: composeUnique(
      [
        `Na defesa de ROI com ${primaryPlayer}, conecte ganho técnico a resultado econômico por hectare.`,
        ...(cortevaHooks?.roiHooks ?? []),
        ...playbook.hooks.roiHooks
      ],
      3
    )
  };
};

type ProducerProfile = 'conservador' | 'moderado' | 'inovador';
type TechLevel = 'baixo' | 'medio' | 'alto';
type PriceSensitivity = 'alta' | 'media' | 'baixa';
type PremiumResistance = 'alta' | 'media' | 'baixa';

const inferCultureStage = (normalizedScenario: string): 'plantio' | 'vegetativo' | 'reprodutivo' | 'enchimento' | 'fertirrigacao' => {
  if (/fertirrigacao|pivo|irrigad|gotejo/.test(normalizedScenario)) return 'fertirrigacao';
  if (/enchimento|grao|classificacao|qualidade final/.test(normalizedScenario)) return 'enchimento';
  if (/florada|reprodutiv|pegamento/.test(normalizedScenario)) return 'reprodutivo';
  if (/vegetativ|cobertura|nitrogenio/.test(normalizedScenario)) return 'vegetativo';
  return 'plantio';
};

const inferProducerProfile = (analysis: ScenarioAnalysis, normalizedScenario: string): ProducerProfile => {
  if (/inovad|tecnologia|alto desempenho|diferencial/.test(normalizedScenario)) return 'inovador';
  if (analysis.foco === 'preco' || /resistent|tradicional|conservador/.test(normalizedScenario)) return 'conservador';
  return 'moderado';
};

const inferTechLevel = (analysis: ScenarioAnalysis, normalizedScenario: string): TechLevel => {
  if (analysis.machineTechTerms.length >= 2 || /isobus|rtk|telemetria|taxa variavel|fertirrigacao/.test(normalizedScenario)) {
    return 'alto';
  }
  if (/plantadeira|pulverizador|monitor|mapa|protocolo/.test(normalizedScenario)) return 'medio';
  return 'baixo';
};

const inferPriceSensitivity = (analysis: ScenarioAnalysis, normalizedScenario: string): PriceSensitivity => {
  if (analysis.foco === 'preco' || /preco|desconto|barato|custo/.test(normalizedScenario)) return 'alta';
  if (/orcamento|caixa|investimento/.test(normalizedScenario)) return 'media';
  return 'baixa';
};

const inferPremiumResistance = (analysis: ScenarioAnalysis, normalizedScenario: string): PremiumResistance => {
  if (/resistente|nao quer premium|nao confia|cetic/.test(normalizedScenario)) return 'alta';
  if (analysis.prioridade === 'competicao' || inferPriceSensitivity(analysis, normalizedScenario) === 'alta') return 'media';
  return 'baixa';
};

const inferClimate = (normalizedScenario: string): 'seco' | 'chuvoso' | 'irrigado' | 'alta_amplitude' | 'normal' => {
  if (/irrigad|pivo|gotejo/.test(normalizedScenario)) return 'irrigado';
  if (/seca|veranico|estiagem|deficit hidrico/.test(normalizedScenario)) return 'seco';
  if (/chuva|chuvoso|excesso hidrico/.test(normalizedScenario)) return 'chuvoso';
  if (/amplitude|oscilacao termica/.test(normalizedScenario)) return 'alta_amplitude';
  return 'normal';
};

const inferSoil = (normalizedScenario: string): 'arenoso' | 'argiloso' | 'media_textura' | 'normal' => {
  if (/arenoso|areia/.test(normalizedScenario)) return 'arenoso';
  if (/argiloso|argila/.test(normalizedScenario)) return 'argiloso';
  if (/media textura|textura media/.test(normalizedScenario)) return 'media_textura';
  return 'normal';
};

const inferPrimaryObjective = (normalizedScenario: string): 'increase_yield' | 'reduce_risk' | 'improve_quality' | 'precision' | 'efficiency' => {
  if (/qualidade|classificacao|padrao comercial|valor de venda/.test(normalizedScenario)) return 'improve_quality';
  if (/risco|estabilidade|previsibilidade|seguranca/.test(normalizedScenario)) return 'reduce_risk';
  if (/precisao|controle fino|fertirrigacao/.test(normalizedScenario)) return 'precision';
  if (/eficiencia|retorno por hectare|roi/.test(normalizedScenario)) return 'efficiency';
  return 'increase_yield';
};

const suggestYaraLinesByDecisionPriority = (
  analysis: ScenarioAnalysis,
  normalizedScenario: string
): string[] => {
  const score = new Map<string, number>();
  const addScore = (lines: string[], weight: number) => {
    lines.forEach((line) => score.set(line, (score.get(line) ?? 0) + weight));
  };

  addScore(YARA_CROP_FOCUS[analysis.cultura] ?? YARA_CROP_FOCUS.geral, 4);

  YARA_STAGE_TO_LINES.forEach((rule) => {
    if (rule.regex.test(normalizedScenario)) addScore(rule.lines, 3);
  });
  YARA_PROBLEM_TO_LINES.forEach((rule) => {
    if (rule.regex.test(normalizedScenario)) addScore(rule.lines, 4);
  });
  YARA_OBJECTIVE_TO_LINES.forEach((rule) => {
    if (rule.regex.test(normalizedScenario)) addScore(rule.lines, 3);
  });

  const climate = inferClimate(normalizedScenario);
  if (climate === 'seco') addScore(['YaraAmplix', 'YaraVita'], 2);
  if (climate === 'chuvoso') addScore(['YaraMila', 'YaraBela', 'YaraVita'], 2);
  if (climate === 'irrigado') addScore(['YaraTera', 'YaraLiva', 'YaraVita'], 3);
  if (climate === 'alta_amplitude') addScore(['YaraAmplix', 'YaraVita'], 2);

  const soil = inferSoil(normalizedScenario);
  if (soil === 'arenoso') addScore(['YaraAmplix', 'YaraTera', 'YaraVita'], 2);
  if (soil === 'argiloso') addScore(['YaraMila', 'YaraAmplix', 'YaraVita'], 2);
  if (soil === 'media_textura') addScore(['YaraMila', 'YaraVita', 'YaraAmplix'], 1);

  const objective = inferPrimaryObjective(normalizedScenario);
  if (objective === 'increase_yield') addScore(['YaraMila', 'YaraVita', 'YaraAmplix'], 2);
  if (objective === 'reduce_risk') addScore(['YaraAmplix', 'YaraTera'], 2);
  if (objective === 'improve_quality') addScore(['YaraLiva', 'YaraVita'], 2);
  if (objective === 'precision') addScore(['YaraTera', 'YaraRega'], 2);
  if (objective === 'efficiency') addScore(['YaraMila', 'YaraBela', 'YaraVera'], 2);

  return [...score.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([line]) => line)
    .slice(0, 5);
};

const buildEngineInsights = (
  analysis: ScenarioAnalysis,
  scenario: string,
  playerHooks: StrategicHooks,
  productFocus?: ProductIntelligence
): PreparationResult['engineInsights'] => {
  const player = productFocus?.fabricante ?? analysis.fabricantes[0] ?? 'C.Vale';
  const normalizedScenario = normalizeText(scenario);
  const cultureBenchmark = CROP_BENCHMARKS[analysis.cultura] ?? CROP_BENCHMARKS.geral;
  const cultureStage = inferCultureStage(normalizedScenario);
  const producerProfile = inferProducerProfile(analysis, normalizedScenario);
  const techLevel = inferTechLevel(analysis, normalizedScenario);
  const priceSensitivity = inferPriceSensitivity(analysis, normalizedScenario);
  const premiumResistance = inferPremiumResistance(analysis, normalizedScenario);
  const cortevaByScenario = player === 'Corteva' ? inferCortevaSolutionsByScenario(normalizedScenario, analysis.cultura) : [];

  const recommendedLines =
    player === 'Yara'
      ? suggestYaraLinesByDecisionPriority(analysis, normalizedScenario)
      : player === 'Corteva'
        ? cortevaByScenario.map((item) => item.label)
      : (PLAYER_VALUE_PLAYBOOKS.find((item) => item.fabricante === player)?.linhas.map((item) => item.linha) ?? []);

  const gains = recommendedLines
    .map((line) => YARA_LINE_GAIN_PERCENT[line])
    .filter((item): item is { min: number; max: number } => Boolean(item));

  const avgGainPercent = gains.length
    ? {
        min: gains.reduce((acc, g) => acc + g.min, 0) / gains.length,
        max: gains.reduce((acc, g) => acc + g.max, 0) / gains.length
      }
    : { min: 3, max: 7 };

  const baseGainMin = (cultureBenchmark.avgYield * avgGainPercent.min) / 100;
  const baseGainMax = (cultureBenchmark.avgYield * avgGainPercent.max) / 100;

  const roiText = (multiplier: number, label: string): string => {
    const gainMin = baseGainMin * multiplier;
    const gainMax = baseGainMax * multiplier;
    const revenueMin = gainMin * cultureBenchmark.pricePerBag;
    const revenueMax = gainMax * cultureBenchmark.pricePerBag;

    return `${label}: potencial de +${gainMin.toFixed(1)} a +${gainMax.toFixed(1)} ${cultureBenchmark.unit}, com receita incremental estimada entre R$ ${revenueMin.toFixed(0)} e R$ ${revenueMax.toFixed(0)} por hectare.`;
  };

  const profileText: Record<ProducerProfile, string> = {
    conservador:
      'Perfil conservador: prioriza segurança e tende a evitar soluções de maior valor agregado sem prova prática; conduza com linguagem simples, validação em área menor e foco em risco evitado.',
    moderado:
      'Perfil moderado: aberto a evolução de manejo quando enxerga lógica de retorno; combine argumento técnico e econômico com passos de implantação claros.',
    inovador:
      'Perfil inovador: busca diferencial e alta performance; conduza por teto produtivo, eficiência fina e captura de valor por hectare.'
  };

  const abordagemPorPreco: Record<PriceSensitivity, string> = {
    alta:
      'Abordagem comercial: começar pelo custo da ineficiência e traduzir em sacas/ha e margem, antes de discutir produto.',
    media:
      'Abordagem comercial: equilibrar investimento e retorno, mostrando alavancas práticas de ganho.',
    baixa:
      'Abordagem comercial: enfatizar performance máxima, previsibilidade e diferenciação competitiva.'
  };

  const abordagemPorResistencia: Record<PremiumResistance, string> = {
    alta:
      'Sequência recomendada: cenário -> problema -> impacto -> custo oculto -> risco de manter o modelo atual -> pequeno reframe -> solução.',
    media:
      'Sequência recomendada: cenário -> lacuna -> oportunidade -> solução -> ROI.',
    baixa:
      'Sequência recomendada: oportunidade -> ganho -> otimização -> solução -> escala.'
  };

  const cvaleCultureAnchor =
    'Âncora CVale: comunicar com Segurança em Primeiro Lugar, Credibilidade técnica, foco em Resultado, geração de valor ao cliente/associado e Mente Aberta para evolução contínua.';

  return {
    analiseCenario:
      `Cenário com foco em ${describeThemeFocus(analysis.primaryTheme)} na fase ${cultureStage}, com prioridade comercial em ${analysis.prioridade}. A recomendação é conduzir por diagnóstico e valor total gerado. ${cvaleCultureAnchor}`,
    hipotesePrincipal:
      `Hipótese principal: a operação está deixando resultado na mesa por critério de decisão incompleto (foco excessivo em custo inicial), e pode recuperar valor ao alinhar técnica + ROI por hectare.`,
    problemasOcultos: [
      'Critério de compra orientado por preço de entrada, sem leitura do custo total do ciclo.',
      'Baixa conexão entre decisão técnica e impacto em margem, risco e previsibilidade.',
      'Potencial subutilização de tecnologia/manejo por falta de narrativa econômica clara.'
    ],
    impactoAgronomico: [
      `Maior consistência do manejo em ${describeThemeFocus(analysis.primaryTheme)} e menor variabilidade entre talhões.`,
      `Melhor resposta técnica na fase ${cultureStage}, protegendo o potencial produtivo.`,
      'Redução de perdas por decisão tardia e execução desalinhada.'
    ],
    impactoEconomico: [
      'Proteção de margem por redução de ineficiência e risco operacional.',
      'Maior previsibilidade de resultado por hectare para tomada de decisão.',
      'Evolução de retorno por área sem depender de guerra de preços.'
    ],
    leituraPerfilProdutor:
      `${profileText[producerProfile]} Nível tecnológico inferido: ${techLevel}. Sensibilidade a preço: ${priceSensitivity}. Resistência a soluções de maior valor agregado: ${premiumResistance}.`,
    estrategiaAbordagem:
      `${abordagemPorPreco[priceSensitivity]} ${abordagemPorResistencia[premiumResistance]} ${playerHooks.opcProcess[0] ?? ''}`.trim(),
    senoideVendaValor: {
      cenario: 'Abrir com escuta ativa e diagnóstico de campo para definir objetivo econômico da reunião.',
      problema: 'Revelar lacunas do manejo atual e como elas pressionam produtividade, margem e previsibilidade.',
      reframe:
        'Reposicionar a decisão: não é custo do item, é custo da ineficiência no ciclo completo.',
      impacto:
        'Traduzir consequência técnica em impacto econômico (sacas, risco, caixa e oportunidade perdida).',
      solucao:
        `Conectar a solução de maior valor agregado da ${player} ao problema priorizado com lógica de execução e retorno por hectare.`,
      compromisso:
        'Fechar próximo passo com área/piloto, prazo, responsável e critério de validação econômico-técnica.'
    },
    reframeChallenger:
      `Reframe Challenger: “o barato na entrada pode sair caro na colheita; decisão boa é a que protege resultado e margem no sistema inteiro.” ${playerHooks.epaEducar[0] ?? ''}`.trim(),
    solucoesPremiumRecomendadas:
      recommendedLines.length > 0
        ? recommendedLines.map((line) => {
            if (player === 'Yara') {
              return `${line}: ${YARA_LINE_ROLE_MAP[line as keyof typeof YARA_LINE_ROLE_MAP]?.role ?? 'linha estratégica'} (${YARA_LINE_ROLE_MAP[line as keyof typeof YARA_LINE_ROLE_MAP]?.use ?? 'uso recomendado conforme cenário'})`;
            }

            if (player === 'Corteva') {
              const match = cortevaByScenario.find((item) => item.label === line);
              return `${line}: ${match?.value.slice(0, 2).join(' | ') ?? 'solução de sistema orientada a proteção de potencial e margem'}`;
            }

            return `${line}: linha estratégica para este cenário`;
          })
        : [`Solução de maior valor agregado ${player}: combinar recomendação técnica com critério econômico para sustentação da decisão.`],
    logicaRoi: {
      cenarioConservador: roiText(ROI_SCENARIO_MULTIPLIERS.conservador, 'Cenário conservador'),
      cenarioModerado: roiText(ROI_SCENARIO_MULTIPLIERS.moderado, 'Cenário moderado'),
      cenarioAgressivo: roiText(ROI_SCENARIO_MULTIPLIERS.agressivo, 'Cenário agressivo')
    },
    proximosPassos: [
      'Validar em área menor com indicador de produtividade, risco e custo por hectare.',
      'Definir em reunião o critério de decisão final (técnico + econômico + execução).',
      'Agendar retorno com data e responsável para decisão de escala.',
      'Reforçar na próxima visita os pilares culturais CVale na conversa: segurança, credibilidade, resultado, valor ao associado e mentalidade de evolução.'
    ]
  };
};

const buildExecutiveFocus = (analysis: ScenarioAnalysis): string[] => {
  const context = CULTURA_CONTEXT[analysis.cultura];
  const base = `Conduzir conversa de negócio com protagonismo, conectando decisão comercial a margem, risco e previsibilidade na ${context.nome}.`;

  if (analysis.prioridade === 'competicao') {
    return pick(
      composeUnique(
        [
          `Reenquadrar a decisão para valor total e risco operacional na ${context.nome}, tirando a negociação da comparação rasa de preço unitário.`,
          'Acordar critério de decisão que combine resultado técnico, impacto econômico e segurança de execução.',
          'Fechar avanço concreto com prazo e responsável, sem deixar a conversa voltar para desconto automático.'
        ],
        3
      ),
      3
    );
  }

  if (analysis.prioridade === 'expansao') {
    return pick(
      composeUnique(
        [
          `Converter confiança técnica em expansão de escopo, com critério econômico claro e impacto mensurável na ${context.nome}.`,
          'Definir piloto ou etapa de expansão com indicador de valor acordado com o produtor.',
          'Conectar ampliação de portfólio ao objetivo de negócio da fazenda, não a empilhamento de produto.'
        ],
        3
      ),
      3
    );
  }

  if (analysis.prioridade === 'retencao') {
    return pick(
      composeUnique(
        [
          `Usar resultado já entregue como argumento de negócio para retenção e avanço de relacionamento sem erosão de margem.`,
          'Mapear próxima alavanca de valor para evitar renovação por inércia e disputa de preço.',
          'Sair com plano de continuidade que preserve resultado e fortaleça parceria de longo prazo.'
        ],
        3
      ),
      3
    );
  }

  if (analysis.prioridade === 'diagnostico') {
    return pick(
      composeUnique(
        [
          'Estabelecer diagnóstico executivo que revele custo oculto, prioridade real e caminho de decisão orientado a retorno.',
          'Construir confiança com leitura prática do campo e tradução para impacto econômico.',
          'Fechar próximo encontro com dados mínimos e regra de decisão já combinada.'
        ],
        3
      ),
      3
    );
  }

  return pick(
    composeUnique(
      [
        base,
        'Elevar o critério da conversa de preço de item para custo total e resultado final da safra.',
        'Garantir avanço concreto com compromisso prático, data e dono da próxima ação.'
      ],
      3
    ),
    3
  );
};

const sentenceLower = (text: string): string => {
  const clean = text.trim().replace(/[?!.]+$/g, '');
  if (!clean) return '';
  return clean.charAt(0).toLowerCase() + clean.slice(1);
};

type ScenarioIntent = {
  desiredShift: string | null;
  desiredCommitment: string | null;
  painSignal: string | null;
  contextSignal: string | null;
};

const cleanIntentChunk = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const cleaned = value
    .replace(/\s+/g, ' ')
    .replace(/^[,;:\-\s]+/, '')
    .replace(/[.?!]+$/g, '')
    .trim();
  return cleaned || null;
};

const extractScenarioIntent = (scenario: string): ScenarioIntent => {
  const compact = scenario.replace(/\s+/g, ' ').trim();
  const contextSignal = cleanIntentChunk(compact.split(/[.?!]/)[0]?.slice(0, 180) ?? null);
  const shiftMatch = compact.match(/\b(?:quero|preciso)\s+(?:conduzir|levar)\s+para\s+([^.;]+)/i);
  const closeMatch = compact.match(/\bfechar\s+(?:um|uma|o|a)?\s*([^.;]+)/i);
  const weakPainPattern = /^(de forma pontual|pontual|como esta|manter como esta|isso|o mesmo)$/i;
  const painCandidates = [...compact.matchAll(/\b(?:mas|por[eé]m)\s+([^.;]+)/gi)]
    .map((match) => cleanIntentChunk(match[1]))
    .filter((value): value is string => Boolean(value))
    .filter((value) => value.length >= 18 && !weakPainPattern.test(value));

  return {
    desiredShift: cleanIntentChunk(shiftMatch?.[1] ?? null),
    desiredCommitment: cleanIntentChunk(closeMatch?.[1] ?? null),
    painSignal: painCandidates[0] ?? null,
    contextSignal
  };
};

const normalizePainSignal = (painSignal: string | null): string | null => {
  if (!painSignal) return null;

  const cleaned = cleanIntentChunk(painSignal)
    ?.replace(/^que\s+/i, '')
    .replace(/^o produtor\s+/i, '')
    .replace(/^a produtora\s+/i, '')
    .replace(/^ele\s+/i, '')
    .replace(/^ela\s+/i, '')
    .trim();

  if (!cleaned) return null;

  const replacements: Array<[RegExp, string]> = [
    [/^questiona se o retorno (realmente )?compensa em todos os talh[oõ]es/i, 'a dúvida sobre o retorno real em todos os talhões'],
    [/^duvida se o retorno (realmente )?compensa em todos os talh[oõ]es/i, 'a dúvida sobre o retorno real em todos os talhões'],
    [/^questiona se\s+/i, 'a dúvida sobre '],
    [/^duvida se\s+/i, 'a dúvida sobre '],
    [/^acha que\s+/i, 'a percepção de que '],
    [/^diz que\s+/i, 'a percepção de que '],
    [/^reclama que\s+/i, 'a reclamação de que '],
    [/^continua decidindo\s+/i, 'continuar decidindo '],
    [/^continua comprando\s+/i, 'continuar comprando '],
    [/^continua usando\s+/i, 'continuar usando '],
    [/^procura a cooperativa apenas quando\s+/i, 'procurar a cooperativa apenas quando '],
    [/^deixa o manejo andar so quando\s+/i, 'deixar o manejo andar só quando '],
    [/^usa tudo de forma parcial\b/i, 'usar a tecnologia só de forma parcial'],
    [/^nao viu retorno\b/i, 'não ver retorno claro'],
    [/^não viu retorno\b/i, 'não ver retorno claro']
  ];

  const normalized = replacements.reduce((text, [pattern, replacement]) => text.replace(pattern, replacement), cleaned);
  return normalized.replace(/\s+/g, ' ').trim();
};

const openSpinQuestion = (question: string): string => {
  const trimmed = question.trim().replace(/\s+/g, ' ');
  const withoutMark = trimmed.replace(/[?]+$/g, '');
  const normalizedStart = normalizeText(withoutMark);
  const yesNoStart = /^(voce|voces|tem|ha|existe|ja|foi|esta|estao|consegue|conseguem|pode|podem|deve|devem)\b/i;

  // Preserve naturally open time-framed questions.
  if (/^ha quanto tempo\b/i.test(normalizedStart)) {
    return `${withoutMark}?`;
  }

  if (/^(voce|voces)\b/i.test(normalizedStart)) {
    const converted = withoutMark.replace(/^(Você|Voce|Vocês|Voces)\b/i, (match) =>
      /s$/i.test(match) ? 'Como vocês' : 'Como você'
    );
    return `${converted}?`;
  }

  if (yesNoStart.test(normalizedStart)) {
    return `Como ${sentenceLower(withoutMark)}?`;
  }

  return trimmed.endsWith('?') ? trimmed : `${trimmed}?`;
};

const detectScenarioChallengeFocus = (normalizedScenario: string, theme: Theme): string => {
  if (
    /fungicida|inseticida|fitossanitari|controle de pragas/.test(normalizedScenario) &&
    /desconto|preco|barato|nao pode subir investimento/.test(normalizedScenario)
  ) {
    return 'tirar a decisão de desconto em fungicida/inseticida e levar para eficácia de controle, custo total e timing de aplicação';
  }

  if (/dessec|pre[-\s]?plantio|pre[-\s]?colheita/.test(normalizedScenario)) {
    return 'qualidade e timing da dessecação para proteger janela operacional';
  }
  if (/clima|instavel|veranico|seca|chuva/.test(normalizedScenario)) {
    return 'reduzir vulnerabilidade da operação diante da instabilidade climática';
  }
  if (/atraso|janela|tarde|urgente|prazo/.test(normalizedScenario)) {
    return 'evitar atraso de decisão e proteger execução na janela crítica';
  }
  if (/preco|desconto|barato|cotacao/.test(normalizedScenario)) {
    return 'tirar a decisão da comparação por preço e migrar para custo total e resultado';
  }
  if (/resisten|cetic|nao acredita|duvida/.test(normalizedScenario)) {
    return 'reduzir resistência com validação prática e critério de decisão objetivo';
  }

  const byTheme: Record<Theme, string> = {
    adubo: 'eficiência nutricional com retorno por hectare',
    defensivo: 'controle estável com risco operacional sob controle',
    semente: 'escolha de genética alinhada a potencial e estabilidade',
    biologico: 'adoção de biológicos com consistência de execução',
    irrigacao: 'eficiência hídrica e energética no momento certo',
    maquinario: 'execução operacional com tecnologia embarcada',
    servicos: 'serviço técnico que acelera decisão e execução',
    geral: 'resultado técnico-econômico da operação'
  };

  return byTheme[theme];
};

const detectProductSalesChallenge = (
  analysis: ScenarioAnalysis,
  normalizedScenario: string,
  theme: Theme,
  productFocus?: ProductIntelligence
): string => {
  const productLabel =
    productFocus?.produto ??
    (theme === 'adubo'
      ? 'adubação'
      : theme === 'defensivo'
        ? 'defensivos'
        : theme === 'semente'
          ? 'sementes'
          : theme === 'biologico'
            ? 'biológicos'
            : theme === 'maquinario'
              ? 'máquinas e tecnologia'
              : theme === 'servicos'
                ? 'serviço técnico'
                : 'solução proposta');

  const priceBarrier = /desconto|preco|barato|nao pode subir investimento|cotacao/.test(normalizedScenario);
  const trustBarrier = /desconfi|nao acredita|resisten|sempre fez assim/.test(normalizedScenario);
  const executionBarrier = /atras|timing|falha|retrabalho|execucao|aplicacao/.test(normalizedScenario);

  if (priceBarrier && executionBarrier) {
    return `defender ${productLabel} sem cair em guerra de preço, provando custo total, eficácia de campo e timing de execução`;
  }
  if (priceBarrier) {
    return `defender ${productLabel} por valor percebido no resultado, e não pelo menor preço de entrada`;
  }
  if (trustBarrier) {
    return `reduzir resistência à venda de ${productLabel} com validação prática e critério técnico-econômico`;
  }
  if (executionBarrier) {
    return `conectar a venda de ${productLabel} à execução correta para transformar recomendação em resultado real`;
  }
  if (analysis.foco === 'preco') {
    return `tirar ${productLabel} da comparação rasa de preço e levar para retorno por hectare`;
  }

  return `aumentar conversão de ${productLabel} com argumento técnico, econômico e plano de execução claro`;
};

const buildScenarioExecutionContext = (
  analysis: ScenarioAnalysis,
  scenario: string,
  productFocus?: ProductIntelligence
): {
  scenarioContext: string;
  challengeFocus: string;
  productSalesChallenge: string;
  stageText: string;
  areaText: string | null;
  prazoText: string | null;
} => {
  const normalizedScenario = normalizeText(scenario);
  const conversationTheme = resolveConversationTheme(analysis, productFocus);
  const themeFocus = describeThemeFocus(conversationTheme);
  const areaText = scenario.match(/\b\d{1,5}(?:[.,]\d+)?\s*ha\b/i)?.[0] ?? null;
  const prazoText = scenario.match(/\b\d{1,3}\s*(dias?|semanas?|mes(?:es)?)\b/i)?.[0] ?? null;
  const stageMap: Record<string, string> = {
    plantio: 'janela de plantio',
    vegetativo: 'fase vegetativa',
    reprodutivo: 'fase reprodutiva',
    enchimento: 'enchimento de grãos',
    fertirrigacao: 'operação irrigada/fertirrigada'
  };
  const stageText = stageMap[inferCultureStage(normalizedScenario)] ?? 'momento crítico do ciclo';
  const challengeFocus = detectScenarioChallengeFocus(normalizedScenario, conversationTheme);
  const productSalesChallenge = detectProductSalesChallenge(analysis, normalizedScenario, conversationTheme, productFocus);
  const contextParts = [
    `foco em ${themeFocus}`,
    `cultura ${analysis.cultura}`,
    stageText,
    areaText ? `área ${areaText}` : null,
    prazoText ? `prazo ${prazoText}` : null
  ].filter(Boolean);

  return {
    scenarioContext: contextParts.join(', '),
    challengeFocus,
    productSalesChallenge,
    stageText,
    areaText,
    prazoText
  };
};

type ScenarioPracticalSignals = {
  familyTradition: boolean;
  noSoilAnalysis: boolean;
  oldAreaLoss: boolean;
  wantsDiagnosticByTalhao: boolean;
  wantsValidationArea: boolean;
  wantsFirstSale: boolean;
  loyaltyToCurrentSupplier: boolean;
  priceBarrier: boolean;
};

const buildScenarioPracticalSignals = (normalizedScenario: string): ScenarioPracticalSignals => ({
  familyTradition: /historico familiar|hist[oó]rico familiar|tradicao da familia|tradic[aã]o da fam[ii]lia|o pai sempre comprou|fam[ii]lia/.test(
    normalizedScenario
  ),
  noSoilAnalysis: /nao tem analise de solo|nao fez analise|analise de solo desatualizada|analise antiga|sem analise de solo/.test(
    normalizedScenario
  ),
  oldAreaLoss: /areas antigas|area antiga|queda de resposta|perda de resposta|solo cansado|fertilidade em queda/.test(
    normalizedScenario
  ),
  wantsDiagnosticByTalhao: /diagnostico por talhao|diagn[oó]stico por talh[aã]o|por talhao|por talh[aã]o/.test(normalizedScenario),
  wantsValidationArea: /area de validacao|área de validação|teste comparativo|area piloto|área piloto|validacao/.test(normalizedScenario),
  wantsFirstSale: /primeira venda|porta de entrada|abrir espaco com uma primeira venda|abrir espaço com uma primeira venda/.test(
    normalizedScenario
  ),
  loyaltyToCurrentSupplier: /sempre da mesma cooperativa|mesma cooperativa|ha anos de outra cooperativa|h[aá] anos de outra cooperativa|fornecedor atual|nao ve motivo para mudar|n[aã]o v[eê] motivo para mudar|fiel/.test(
    normalizedScenario
  ),
  priceBarrier: /preco|desconto|barato|cotacao|cota[cç][aã]o/.test(normalizedScenario)
});

const buildCustomerPainFocus = (
  analysis: ScenarioAnalysis,
  normalizedScenario: string,
  challengeFocus: string,
  scenarioIntent: ScenarioIntent
): string => {
  const rawPain = normalizePainSignal(scenarioIntent.painSignal) ?? '';

  if (analysis.primaryTheme === 'maquinario') {
    if (/usa tudo de forma parcial|subutilizada|nao viu retorno|n[aã]o viu retorno|investiu muito/.test(normalizedScenario)) {
      return 'ter tecnologia embarcada na fazenda sem transformar isso em rotina, leitura e resultado no campo';
    }
    return 'ter tecnologia e execução sem um padrão claro que vire resultado no campo';
  }

  if (rawPain) {
    if (/usa tudo de forma parcial|continua decidindo mais por relacionamento e preco de entrada|procura a cooperativa apenas quando surge praga|deixa o manejo andar so quando a praga/.test(normalizedScenario)) {
      return challengeFocus;
    }
    return rawPain;
  }

  if (analysis.primaryTheme === 'adubo') {
    if (/analise de solo|analise antiga|sem analise/.test(normalizedScenario)) {
      return 'seguir decidindo a adubação sem análise de solo atualizada e sem leitura por talhão';
    }
    if (/tradicao|familia|pai sempre comprou|historico familiar/.test(normalizedScenario)) {
      return 'manter a compra de adubo mais pelo hábito do que pela resposta real das áreas';
    }
    return 'manter uma adubação que já não responde do mesmo jeito em todas as áreas';
  }

  if (analysis.primaryTheme === 'defensivo') {
    if (/surge praga|falha de controle|em cima da hora|ultimo momento|último momento/.test(normalizedScenario)) {
      return 'deixar o manejo andar só quando a praga ou a falha de controle já apareceu';
    }
    return 'seguir com um manejo fitossanitário mais reativo do que planejado';
  }

  if (analysis.primaryTheme === 'semente') {
    return 'seguir escolhendo semente sem um critério claro de ambiente, risco e resposta esperada';
  }

  if (/desconfi|promessa|ruido comercial|ruído comercial/.test(normalizedScenario)) {
    return 'deixar boas recomendações de lado por falta de confiança e critério de validação';
  }

  return challengeFocus;
};

const buildOpcProcessOpeningLines = (
  normalizedPainSignal: string | null,
  customerPainFocus: string
): string[] => {
  if (!normalizedPainSignal) {
    return [
      `Abra pela realidade do produtor e entenda em que momento ${customerPainFocus} começa a pesar no resultado.`,
      'Aprofunde o contexto com calma para separar sintoma, causa e efeito antes de levar a conversa para a proposta.'
    ];
  }

  return [
    `Comece pela situação real da fazenda e explore como ${normalizedPainSignal} aparece hoje no campo e na decisão.`,
    `Aprofunde onde esse ponto nasce, como ele se repete e em que momento passa a travar resultado, margem ou previsibilidade.`
  ];
};

const buildProblemDeepDivePrompts = (
  analysis: ScenarioAnalysis,
  normalizedScenario: string,
  customerPainFocus: string
): string[] => {
  if (analysis.primaryTheme === 'maquinario') {
    if (/piloto|mapas|taxa variavel|taxa variável/.test(normalizedScenario)) {
      return [
        'Onde piloto, mapas e taxa variavel entram só parcialmente na rotina e acabam perdendo valor no campo?',
        'Em que parte do processo a tecnologia embarcada deixa de virar decisao pratica e resultado?',
        'O que mais trava hoje o uso completo da tecnologia: equipe, leitura de dados, regulagem ou disciplina de execucao?'
      ];
    }
    return [
      `Onde ${customerPainFocus} começa a travar eficiencia e qualidade de execucao?`,
      'Em que etapa da operacao a tecnologia deixa de gerar o retorno que prometia?'
    ];
  }

  if (analysis.primaryTheme === 'adubo') {
    return [
      `Onde ${customerPainFocus} aparece primeiro: escolha da formula, dose, prioridade de area ou leitura do solo?`,
      'Em que talhoes a decisao atual mais claramente deixa de responder como deveria?'
    ];
  }

  if (analysis.primaryTheme === 'defensivo') {
    return [
      `Onde ${customerPainFocus} aparece primeiro: timing, escolha do produto, pressao da praga ou qualidade da aplicacao?`,
      'Em que momento a operacao sai do planejamento e entra no modo reativo?'
    ];
  }

  return [
    `Onde ${customerPainFocus} comeca a ficar mais visivel na rotina da fazenda?`,
    'Em que parte da operacao o problema deixa de ser pontual e passa a comprometer o resultado?'
  ];
};

const buildImplicationEscalationPrompts = (
  analysis: ScenarioAnalysis,
  customerPainFocus: string,
  areaText: string | null
): string[] => {
  const totalArea = parseAreaHectares(areaText);
  const totalAreaLabel = totalArea ? `nos ${formatHectares(totalArea)}` : 'na area toda';

  if (analysis.primaryTheme === 'maquinario') {
    return [
      `Se ${customerPainFocus} continuar, quanto dessa tecnologia segue imobilizada sem devolver resultado ${totalAreaLabel}?`,
      'Quanto custa investir em piloto, mapas e taxa variavel e usar isso so pela metade em vez de capturar eficiencia e precisao?',
      'Se a tecnologia embarcada nao vira rotina de equipe, onde a perda pesa mais: operacao, produtividade ou custo por hectare?'
    ];
  }

  if (analysis.primaryTheme === 'adubo') {
    return [
      `Se ${customerPainFocus} continuar, quanto isso pode tirar da margem ${totalAreaLabel}?`,
      'Quanto custa manter uma adubacao tradicional quando a area ja pede mais precisao de leitura e de investimento?'
    ];
  }

  if (analysis.primaryTheme === 'defensivo') {
    return [
      `Se ${customerPainFocus} continuar, quanto isso pode custar em reaplicacao, perda de controle e resultado ${totalAreaLabel}?`,
      'Se a decisao seguir chegando tarde, quanto de produtividade e de margem pode sair da lavoura antes de virar conta?'
    ];
  }

  return [
    `Se ${customerPainFocus} continuar, quanto isso pode tirar do resultado ${totalAreaLabel}?`,
    'Se o problema seguir sem correcao, onde a conta mais pesada aparece: na produtividade, na margem ou no caixa?'
  ];
};

const parseAreaHectares = (areaText: string | null): number | null => {
  if (!areaText) return null;
  const match = areaText.match(/\d{1,5}(?:[.,]\d+)?/);
  if (!match) return null;
  const value = Number(match[0].replace('.', '').replace(',', '.'));
  return Number.isFinite(value) ? value : null;
};

const formatHectares = (value: number): string => `${Math.round(value).toLocaleString('pt-BR')} ha`;

const buildPilotAreaRange = (areaText: string | null): string | null => {
  const totalArea = parseAreaHectares(areaText);
  if (!totalArea || totalArea < 120) return null;

  const lowPct = 0.3;
  const highPct = 0.5;
  const lowArea = formatHectares(totalArea * lowPct);
  const highArea = formatHectares(totalArea * highPct);
  return `entre ${Math.round(lowPct * 100)}% e ${Math.round(highPct * 100)}% da área (${lowArea} a ${highArea})`;
};

const buildImplicationQuantificationPrompts = (
  analysis: ScenarioAnalysis,
  areaText: string | null,
  customerPainFocus: string
): string[] => {
  const totalArea = parseAreaHectares(areaText);
  const totalAreaLabel = totalArea ? `nos ${formatHectares(totalArea)}` : 'na área toda';

  if (analysis.primaryTheme === 'adubo') {
    return [
      `Se ${customerPainFocus} tirar 3, 5 ou 8 sacas por hectare, quanto isso representa em dinheiro ${totalAreaLabel}?`,
      'Se a adubação continuar desalinhada da necessidade real do solo, quanto de margem pode estar ficando na mesa sem aparecer na compra?',
      'Quando a área antiga responde menos e ninguém corrige a rota, quanto custa insistir mais uma safra no mesmo manejo?'
    ];
  }

  if (analysis.primaryTheme === 'defensivo') {
    return [
      `Se ${customerPainFocus} levar a uma reaplicação ou a uma perda de controle, quanto isso pesa por hectare e ${totalAreaLabel}?`,
      'Se a praga ou a doença avançar alguns dias além do ponto, quanto de produtividade e de rentabilidade pode ir embora nessa janela?',
      'Quanto custa para o caixa da fazenda somar aplicação extra, perda de teto produtivo e mais risco operacional na mesma conta?'
    ];
  }

  if (analysis.primaryTheme === 'semente') {
    return [
      `Se ${customerPainFocus} derrubar stand, uniformidade ou teto produtivo, quanto isso representa por hectare e ${totalAreaLabel}?`,
      'Se a escolha do material continuar desalinhada com o ambiente, quanto de produtividade deixa de virar margem no fechamento da safra?',
      'Quanto custa descobrir só depois da emergência que a economia na semente saiu cara demais na área?'
    ];
  }

  return [
    `Se ${customerPainFocus} continuar por mais uma safra, quanto isso pode tirar do resultado ${totalAreaLabel}?`,
    'Se esse problema seguir pressionando manejo, produtividade e execução, onde a conta mais pesada aparece: no custo, na margem ou no caixa?',
    'Quanto custa manter o problema por mais um ciclo em vez de corrigir agora com critério?'
  ];
};

const buildPilotAreaCommitment = (
  analysis: ScenarioAnalysis,
  productFocus: ProductIntelligence | undefined,
  areaText: string | null,
  signals: ScenarioPracticalSignals
): string => {
  const totalArea = parseAreaHectares(areaText);

  if (!totalArea || totalArea < 120) {
    if (analysis.primaryTheme === 'adubo') {
      return 'Fechar um diagnóstico por talhão e uma área piloto pequena, com leitura simples de resposta e retorno por hectare.';
    }
    return 'Fechar uma área piloto pequena, com critério claro de validação antes de ampliar a recomendação.';
  }

  const rangeLabel = buildPilotAreaRange(areaText) ?? 'uma faixa relevante da área';

  if (analysis.primaryTheme === 'adubo') {
    if (signals.wantsDiagnosticByTalhao || signals.noSoilAnalysis || signals.oldAreaLoss) {
      return `Fechar um diagnóstico por talhão e uma validação em ${rangeLabel}, priorizando as áreas mais antigas ou com menor resposta.`;
    }
    return `Fechar uma validação de adubação em ${rangeLabel}, com meta simples de resposta por área e retorno por hectare.`;
  }

  if (analysis.primaryTheme === 'semente') {
    return `Fechar uma primeira área com sementes em ${rangeLabel}, com comparação de stand, uniformidade e resultado por hectare.`;
  }

  if (analysis.primaryTheme === 'defensivo') {
    return `Fechar um ajuste de programa em ${rangeLabel}, com critério claro de controle, risco evitado e leitura de resultado.`;
  }

  if (productFocus) {
    return `Fechar uma validação de ${productFocus.produto} em ${rangeLabel}, com leitura combinada antes de ampliar para o restante da área.`;
  }

  return `Fechar uma validação em ${rangeLabel}, com próximo passo condicionado ao que for medido no campo.`;
};

const buildScenarioDrivenSpinPrompts = (
  analysis: ScenarioAnalysis,
  normalizedScenario: string
): { situacao: string[]; problema: string[]; implicacao: string[]; necessidade: string[] } => {
  const signals = buildScenarioPracticalSignals(normalizedScenario);
  const prompts = {
    situacao: [] as string[],
    problema: [] as string[],
    implicacao: [] as string[],
    necessidade: [] as string[]
  };

  if (analysis.primaryTheme === 'adubo' && signals.familyTradition) {
    prompts.situacao.push(
      'Nessa compra de fertilizante, o que mais pesa hoje na decisão: tradição da família, confiança no fornecedor, condição comercial ou resposta percebida no campo?'
    );
    prompts.problema.push(
      'Quando a compra segue mais pelo hábito do que por critério técnico, onde costuma aparecer a primeira perda de eficiência?'
    );
    prompts.implicacao.push(
      'Se a adubação continuar sendo definida sem revisar solo e resposta por área, onde isso pode pesar mais na margem da safra?'
    );
    prompts.necessidade.push(
      'O que precisaria ficar claro em uma área de validação para você se sentir seguro em comparar esse manejo com o atual?'
    );
  }

  if (analysis.primaryTheme === 'adubo' && signals.noSoilAnalysis) {
    prompts.situacao.push('Sem análise de solo recente, como vocês vêm decidindo dose, fórmula e prioridade de área hoje?');
    prompts.problema.push('Em quais talhões vocês já sentem que o fertilizante não está respondendo como antes?');
    prompts.implicacao.push(
      'Se a decisão continuar sem leitura atualizada do solo, que tipo de erro tende a ficar escondido: dose, fórmula, prioridade de área ou tudo junto?'
    );
    prompts.necessidade.push(
      'Qual seria um bom próximo passo para comparar a recomendação atual com um diagnóstico por talhão, sem aumentar risco desnecessário?'
    );
  }

  if (signals.loyaltyToCurrentSupplier) {
    prompts.situacao.push(
      'O que o fornecedor atual faz bem hoje e que o senhor considera importante preservar numa nova parceria?'
    );
    prompts.problema.push(
      'Onde essa relação atual já não está entregando a mesma evolução de resultado que entregava antes?'
    );
    prompts.implicacao.push(
      'Se nada mudar na forma de decidir, qual oportunidade de ganho pode continuar ficando para depois?'
    );
    prompts.necessidade.push(
      'Que formato de entrada faria sentido para comparar sem mexer em toda a operação de uma vez?'
    );
  }

  if (signals.priceBarrier) {
    prompts.situacao.push('Quando vocês comparam propostas, como colocam na conta o que vai além do preço de entrada?');
    prompts.problema.push('Em que momento o preço mais baixo deixou de ser economia e virou custo maior lá na frente?');
    prompts.implicacao.push('Se a decisão continuar olhando mais para preço do que para resultado, onde a conta tende a aparecer primeiro?');
    prompts.necessidade.push('Que conta precisa ficar simples e objetiva para a decisão sair do preço e ir para valor?');
  }

  return prompts;
};

const buildFirstCommitmentByScenario = (
  analysis: ScenarioAnalysis,
  productFocus: ProductIntelligence | undefined
): string => {
  if (productFocus) {
    return `Fechar um primeiro avanço com ${productFocus.produto}, com critério técnico-econômico acordado antes da execução.`;
  }

  if (analysis.primaryTheme === 'semente') {
    return 'Fechar uma primeira venda de sementes, com meta de estande, vigor e resultado por hectare.';
  }

  if (analysis.primaryTheme === 'defensivo') {
    return 'Fechar um ajuste de programa fitossanitário, com indicador de controle e impacto em risco operacional.';
  }

  if (analysis.primaryTheme === 'adubo') {
    return 'Fechar uma recomendação de adubação com critério claro de resposta e retorno por hectare.';
  }

  if (analysis.primaryTheme === 'maquinario') {
    return 'Fechar uma ação prática de execução, como calibração, operação ou uso de tecnologia embarcada, com ganho operacional mensurável.';
  }

  if (analysis.primaryTheme === 'servicos') {
    return 'Fechar um serviço técnico inicial, com plano de acompanhamento e indicador de resultado.';
  }

  return 'Fechar uma primeira ação de entrada, com objetivo claro de validação de valor para o produtor.';
};

const isCooperativeLoyaltyScenario = (analysis: ScenarioAnalysis, normalizedScenario: string): boolean => {
  if (!analysis.tags.includes('cooperativa')) return false;

  return /outra cooperativa|cooperativa concorrente|concorrente|na concorrente|fornecedor atual|nao quer mudar|n[aã]o quer mudar|fiel.*cooperativa|historico familiar|hist[oó]rico familiar|pai sempre comprou/.test(
    normalizedScenario
  );
};

const isCurrentCvaleRelationshipScenario = (analysis: ScenarioAnalysis, normalizedScenario: string): boolean => {
  if (!analysis.tags.includes('cooperativa')) return false;
  if (isCooperativeLoyaltyScenario(analysis, normalizedScenario)) return false;

  return /procura a cooperativa|procura a c\.?vale|ja compra da cooperativa|j[aá] compra da cooperativa|compra da cooperativa|compra na cooperativa|trabalha com a cooperativa|trabalha com a c\.?vale|ja trabalha com a cooperativa|j[aá] trabalha com a cooperativa|compra pouco da c\.?vale|gosta da assistencia|gosta da assist[eê]ncia|ja recebeu proposta da c\.?vale|j[aá] recebeu proposta da c\.?vale/.test(
    normalizedScenario
  );
};

const buildOpc = (
  analysis: ScenarioAnalysis,
  playerHooks: StrategicHooks,
  productFocus: ProductIntelligence | undefined,
  scenario: string,
  spin: SpinBlock
) => {
  const context = CULTURA_CONTEXT[analysis.cultura];
  const metricFocus = metricByCulture(analysis.cultura);
  const conversationTheme = resolveConversationTheme(analysis, productFocus);
  const themeFocus = describeThemeFocus(conversationTheme);
  const scenarioName = inferAgroScenarioName(scenario);
  const scenarioPlaybook = getAgroScenarioPlaybook(scenarioName);
  const scenarioOpcPlaybook = getAgroScenarioOpcPlaybook(scenarioName);
  const normalizedScenario = normalizeText(scenario);
  const practicalSignals = buildScenarioPracticalSignals(normalizedScenario);
  const coopLoyaltyScenario = isCooperativeLoyaltyScenario(analysis, normalizedScenario);
  const currentCvaleRelationshipScenario = isCurrentCvaleRelationshipScenario(analysis, normalizedScenario);
  const { challengeFocus, productSalesChallenge, areaText, prazoText } = buildScenarioExecutionContext(
    analysis,
    scenario,
    productFocus
  );
  const scenarioIntent = extractScenarioIntent(scenario);
  const customerPainFocus = buildCustomerPainFocus(analysis, normalizeText(scenario), challengeFocus, scenarioIntent);
  const normalizedPainSignal = normalizePainSignal(scenarioIntent.painSignal);
  const opcProcessOpeningLines = buildOpcProcessOpeningLines(normalizedPainSignal, customerPainFocus);

  const spinProblemaResumo = sentenceLower(spin.problema[0] ?? '');

  if (coopLoyaltyScenario) {
    const objetivo = pick(
      composeUnique(
        [
          'Entender com profundidade quais benefícios e vantagens o produtor enxerga na cooperativa atual e quais critérios sustentam essa fidelidade.',
          'Mapear de forma objetiva o que ele não gosta, teme ou ainda não percebe de valor na C.Vale.',
          'Construir abertura de baixo risco para iniciar relação de confiança, priorizando uma primeira decisão prática.'
        ],
        3
      ),
      3
    );

    const processo = pick(
      composeUnique(
        [
          'Faça perguntas abertas para entender histórico, percepção de valor e experiências concretas com a cooperativa atual.',
          `Valide o problema sem confronto: ${spinProblemaResumo || 'o produtor ainda não vê motivo claro para mudar parte das decisões atuais'}.`,
          'Pergunte diretamente o que ele gostaria de manter, o que aceitaria melhorar e o que considera inegociável na parceria atual.',
          'Explore como a C.Vale pode ajudar sem ruptura total: comece por um ponto específico em que o risco percebido é menor.',
          productFocus
            ? `Posicione ${productFocus.produto} como porta de entrada para provar valor com execução e resultado mensurável.`
            : 'Posicione uma solução de entrada (ex.: sementes) com validação simples e critério claro de sucesso.',
          'Conduza por experiência do associado: facilidade de execução, suporte técnico e segurança da decisão.'
        ],
        6
      ),
      6
    );

    const compromisso = pick(
      composeUnique(
        [
          'Fechar uma primeira venda de sementes como projeto de entrada, com área definida e indicador de validação.',
          'Agendar retorno pós-implantação para revisar resultado técnico-econômico e decidir próximo avanço.',
          'Formalizar critério de decisão para a segunda etapa da parceria com base no que foi validado no campo.'
        ],
        3
      ),
      3
    );

    return { objetivo, processo, compromisso };
  }

  if (currentCvaleRelationshipScenario) {
    const objetivo = pick(
      composeUnique(
        [
          'Entender com profundidade quais benefícios da C.Vale o produtor já reconhece hoje e onde ainda existe espaço para ampliar valor.',
          'Mapear o que faz o produtor procurar a C.Vale só em momentos pontuais, e o que falta para a cooperativa entrar mais cedo na decisão.',
          'Construir um próximo avanço simples, útil e de baixo risco para aumentar presença técnica e comercial da C.Vale neste caso.'
        ],
        3
      ),
      3
    );

    const processo = pick(
      composeUnique(
        [
          'Faça perguntas abertas para entender em quais momentos ele aciona a C.Vale, o que ele valoriza nesse apoio e onde a cooperativa ainda chega tarde.',
          `Valide o problema sem confronto: ${spinProblemaResumo || 'a cooperativa entra mais para apagar incêndio do que para prevenir perda de resultado'}.`,
          'Aprofunde o que faria sentido melhorar na atuação da C.Vale antes da dor aparecer no campo.',
          'Mostre como a C.Vale pode sair do papel de apoio pontual e entrar como parceira de planejamento e prevenção.',
          productFocus
            ? `Posicione ${productFocus.produto} dentro de uma lógica simples de programa, rotina e acompanhamento pela C.Vale.`
            : 'Posicione a recomendação como parte de um plano simples, com acompanhamento e critério claro de resultado.',
          'Conduza a conversa para facilidade de execução, segurança da decisão e ganho prático para o produtor.'
        ],
        6
      ),
      6
    );

    const compromisso = pick(
      composeUnique(
        [
          buildPilotAreaCommitment(analysis, productFocus, areaText, practicalSignals),
          'Fechar um próximo passo em que a C.Vale entre antes do problema, com indicador simples e data já combinada para revisão.',
          'Agendar retorno curto para ler resultado, ajustar rota e decidir se a cooperativa amplia participação naquele manejo.'
        ],
        3
      ),
      3
    );

    return { objetivo, processo, compromisso };
  }

  const objectivePool = composeUnique(
    [
      ...(scenarioIntent.desiredShift ? [`Levar a conversa para ${scenarioIntent.desiredShift}.`] : []),
      `Abrir espaço para avançar em ${challengeFocus}.`,
      `Tirar a decisão de ${productSalesChallenge} e levar para critério prático de resultado.`,
      'Transformar o problema principal em uma decisão objetiva de negócio, com foco em resultado no campo e retorno por hectare.',
      `Conduzir a conversa para uma decisão mais segura em ${context.nome}, com base em dado, execução e viabilidade real.`,
      ...(analysis.primaryTheme === 'adubo' && practicalSignals.noSoilAnalysis
        ? ['Abrir espaço para um diagnóstico por talhão que mostre onde a adubação já não está respondendo como antes.']
        : []),
      ...(analysis.primaryTheme === 'adubo' && practicalSignals.familyTradition
        ? ['Entender onde o hábito de compra ainda ajuda e onde já está impedindo uma decisão mais técnica.']
        : []),
      ...(scenarioOpcPlaybook?.objetivo ? [scenarioOpcPlaybook.objetivo] : []),
      ...buildExecutiveFocus(analysis)
    ],
    5
  );

  const processPool = composeUnique(
    [
      ...opcProcessOpeningLines,
      'Faça perguntas abertas para entender como ele decide hoje, o que tem pesado na escolha e onde ele já percebe perda de resultado.',
      'Aprofunde o problema sem generalizar: descubra onde a decisão atual deixa margem, produtividade ou previsibilidade na mesa.',
      `Leve a conversa para implicações práticas em ${metricFocus}, sem transformar a reunião em aula teórica.`,
      `Traduza a recomendação de ${themeFocus} para rotina de campo, janela de execução e condição real da equipe.`,
      productFocus
        ? `Com ${productFocus.produto}, traduza benefício técnico em impacto econômico mensurável no talhão.`
        : `Traduza a recomendação em impacto econômico mensurável no talhão.`,
      'Valide adesão e viabilidade: quem decide, quem executa e o que pode travar o avanço desta decisão.',
      'Alinhe execução segura, procedimento e responsabilidade ambiental antes de formalizar qualquer avanço.',
      'Preserve credibilidade técnica: recomende o que faz sentido para a gleba e para o ciclo, sem empurrar combinação sem base.',
      ...(analysis.primaryTheme === 'adubo' && practicalSignals.noSoilAnalysis
        ? ['Na adubação, compare hábito de compra com leitura de solo, resposta por talhão e prioridade real de investimento.']
        : []),
      ...(analysis.primaryTheme === 'adubo' && practicalSignals.oldAreaLoss
        ? ['Priorize os talhões mais antigos ou de menor resposta para mostrar rapidamente onde o ajuste pode gerar valor.']
        : []),
      ...(scenarioPlaybook?.opcProcesso ? [scenarioPlaybook.opcProcesso] : []),
      ...(scenarioOpcPlaybook?.processo ? [scenarioOpcPlaybook.processo] : []),
      ...playerHooks.opcProcess
    ],
    8
  );

  const firstCommitment = buildFirstCommitmentByScenario(analysis, productFocus);
  const commitmentsPool = composeUnique(
    [
      ...(scenarioIntent.desiredCommitment ? [`Fechar ${scenarioIntent.desiredCommitment}.`] : []),
      firstCommitment,
      buildPilotAreaCommitment(analysis, productFocus, areaText, practicalSignals),
      buildPilotAreaRange(areaText)
        ? `Definir plano de validação em ${buildPilotAreaRange(areaText)} com indicador técnico-econômico e data de leitura acordada.`
        : 'Definir plano de validação em área piloto com indicador técnico-econômico e data de leitura acordada.',
      prazoText
        ? `Formalizar decisão e responsável dentro do prazo crítico já citado (${prazoText}).`
        : 'Formalizar decisão com prazo, responsável e regra objetiva de avanço.',
      'Sair da reunião com regra clara para decidir avanço, manutenção ou expansão da área validada.',
      analysis.foco === 'preco' || analysis.prioridade === 'competicao'
        ? 'Se houver pressão por preço, fechar comparação por custo total e risco antes de discutir qualquer concessão.'
        : 'Fechar próximo passo com compromisso de execução e validação objetiva de resultado.',
      ...(scenarioOpcPlaybook?.compromisso ? [scenarioOpcPlaybook.compromisso] : [])
    ],
    6
  );

  return {
    objetivo: pick(objectivePool, 3),
    processo: pick(processPool, 6),
    compromisso: pick(commitmentsPool, 3)
  };
};

const buildScenarioSpinAnchors = (
  analysis: ScenarioAnalysis,
  scenario: string,
  productFocus?: ProductIntelligence
): { situacao: string; problema: string; implicacao: string; necessidadeSolucao: string } => {
  const normalized = normalizeText(scenario);
  const activeTheme = resolveConversationTheme(analysis, productFocus);

  const isDessecacaoContext =
    /dessec|pre[-\s]?colheita|pre[-\s]?plantio/.test(normalized) || productFocus?.produto.toLowerCase() === 'gapper';

  if (isDessecacaoContext) {
    return {
      situacao:
        'Como vocês têm feito a dessecação até aqui e quais critérios definem o momento e a forma de aplicação?',
      problema:
        'Onde a estratégia atual de dessecação ainda traz variação de resultado, atraso de janela ou retrabalho?',
      implicacao:
        'Quando a dessecação não acontece no momento certo, qual impacto aparece na produtividade, no risco operacional e na margem?',
      necessidadeSolucao:
        'Que condição de resultado você precisa ver para concordar em avançar com um ajuste de manejo na dessecação?'
    };
  }

  if (activeTheme === 'adubo') {
    return {
      situacao:
        'Como vocês têm feito hoje a adubação (produto, dose e momento) e o que pesa mais nessa decisão no dia a dia?',
      problema:
        'Em quais áreas a estratégia atual de adubação ainda não está entregando o retorno esperado por hectare?',
      implicacao:
        'Se a adubação atual estiver deixando produtividade na mesa, quantas sacas por hectare isso pode representar e quanto dinheiro sai da margem da fazenda?',
      necessidadeSolucao:
        'Qual condição de resultado precisa ficar clara para você concordar em ajustar o manejo de adubação?'
    };
  }

  if (activeTheme === 'defensivo') {
    const isFungicideInsecticideDiscountCase =
      /fungicida|inseticida|fitossanitari|controle de pragas/.test(normalized) &&
      /desconto|preco|barato|nao pode subir investimento/.test(normalized);

    if (isFungicideInsecticideDiscountCase) {
      return {
        situacao:
          'Como vocês definem hoje o momento da aplicação de fungicida e inseticida, e o que mais pesa na escolha: preço, eficácia ou risco de falha?',
        problema:
          'Onde a pressão por desconto em fungicida/inseticida já trouxe retrabalho, falha de controle ou insegurança no timing?',
        implicacao:
          'Quando a decisão atrasa ou entra produto pelo menor preço, quanto isso pode custar em reaplicação, perda de controle e produtividade final?',
        necessidadeSolucao:
          'Que critério técnico-econômico precisa ficar acordado hoje para fechar um ajuste de programa com segurança?'
      };
    }

    return {
      situacao:
        'Como vocês têm conduzido hoje o manejo de defensivos (programa, momento e critério de escolha)?',
      problema:
        'Onde o manejo atual ainda gera falha de controle, retrabalho ou custo acima do esperado?',
      implicacao:
        'Se esse padrão continuar no ciclo, quanto ele pode tirar de produtividade, margem e previsibilidade na fazenda?',
      necessidadeSolucao:
        'Que evidência de campo você precisa ver para concordar com um ajuste de programa e avançar com segurança?'
    };
  }

  if (analysis.primaryTheme === 'semente') {
    return {
      situacao:
        'Como vocês têm escolhido hoje sementes/híbridos por ambiente de produção e qual critério mais pesa na decisão final?',
      problema:
        'Em quais áreas o material atual ainda limita estabilidade, teto produtivo ou qualidade de estabelecimento?',
      implicacao:
        'Quando a genética fica desalinhada com o ambiente, quanto isso pode custar em stand, produtividade e retorno por hectare?',
      necessidadeSolucao:
        'Que validação prática você precisa para concordar com um ajuste de material na próxima janela?'
    };
  }

  if (activeTheme === 'maquinario') {
    return {
      situacao:
        'Como vocês têm usado hoje máquinas e implementos no campo e quais tecnologias embarcadas realmente entram na rotina?',
      problema:
        'Onde a operação ainda perde eficiência por calibração, baixa adoção tecnológica ou falha de execução?',
      implicacao:
        'Se isso continuar, qual impacto aparece em custo operacional, produtividade e previsibilidade da safra?',
      necessidadeSolucao:
        'Qual melhoria operacional precisa ficar comprovada para você concordar com a decisão de investimento?'
    };
  }

  if (analysis.primaryTheme === 'irrigacao') {
    return {
      situacao:
        'Como vocês têm feito hoje o manejo de irrigação e quais critérios definem prioridade de área e momento?',
      problema:
        'Onde o manejo atual ainda gera variação de resultado, desperdício de recurso ou risco de estresse da cultura?',
      implicacao:
        'Se esse padrão continuar, qual impacto econômico aparece em custo, produtividade e estabilidade da operação?',
      necessidadeSolucao:
        'Que resultado objetivo você precisa ver para concordar com a mudança de estratégia de irrigação?'
    };
  }

  if (activeTheme === 'biologico') {
    return {
      situacao:
        'Como vocês têm usado hoje biológicos na operação e como validam consistência de resultado em campo?',
      problema:
        'Onde a adoção atual ainda gera dúvida de posicionamento, execução ou retorno econômico?',
      implicacao:
        'Se essa incerteza continuar, qual impacto aparece em eficiência de manejo e margem do sistema?',
      necessidadeSolucao:
        'Que critério de validação você precisa acordar para avançar com mais segurança no uso de biológicos?'
    };
  }

  if (activeTheme === 'servicos') {
    return {
      situacao:
        'Como vocês têm estruturado hoje o suporte técnico e o acompanhamento de campo ao longo do ciclo?',
      problema:
        'Onde esse suporte ainda não está virando decisão mais rápida e resultado econômico na fazenda?',
      implicacao:
        'Se isso continuar, qual custo aparece em decisão tardia, retrabalho e perda de oportunidade?',
      necessidadeSolucao:
        'Que modelo de atendimento faria você concordar com um avanço de escopo nesta parceria?'
    };
  }

  const themeLabel =
    analysis.specificTerms.length ? analysis.specificTerms.slice(0, 2).join(' e ') : describeThemeFocus(analysis.primaryTheme);

  return {
    situacao:
      `Como vocês têm conduzido hoje as decisões ligadas a ${themeLabel} e quais critérios mais pesam na prática?`,
    problema:
      'Onde o modelo atual ainda trava resultado: execução de campo, consistência técnica ou critério de decisão?',
    implicacao:
      'Se esse padrão continuar nas próximas semanas, qual impacto direto aparece em margem, produtividade e previsibilidade?',
    necessidadeSolucao:
      'Qual condição mínima de resultado precisa ficar clara para você concordar em avançar com a proposta?'
  };
};

const buildSpin = (
  analysis: ScenarioAnalysis,
  desafiosAgro: AgroChallengeSignal[],
  scenario: string,
  playerHooks: StrategicHooks,
  productFocus?: ProductIntelligence
) => {
  const context = CULTURA_CONTEXT[analysis.cultura];
  const metricFocus = metricByCulture(analysis.cultura);
  const spinTemplate = inferSpinScenarioTemplate(scenario);
  const conversationTheme = resolveConversationTheme(analysis, productFocus);
  const themeFocus = describeThemeFocus(conversationTheme);
  const byTheme = buildThemeSpinInjections(conversationTheme);
  const byTerms = buildSpecificTermInjections(analysis, conversationTheme);
  const scenarioDrivenPrompts = buildScenarioDrivenSpinPrompts(analysis, normalizeText(scenario));
  const anchors = buildScenarioSpinAnchors(analysis, scenario, productFocus);
  const scenarioIntent = extractScenarioIntent(scenario);
  const customerPainFocus = buildCustomerPainFocus(analysis, normalizeText(scenario), describeThemeFocus(conversationTheme), scenarioIntent);
  const { areaText } = buildScenarioExecutionContext(analysis, scenario, productFocus);
  const problemDeepDivePrompts = buildProblemDeepDivePrompts(
    analysis,
    normalizeText(scenario),
    customerPainFocus
  );
  const quantifiedImplications = buildImplicationQuantificationPrompts(
    analysis,
    areaText,
    customerPainFocus
  );
  const implicationEscalationPrompts = buildImplicationEscalationPrompts(
    analysis,
    customerPainFocus,
    areaText
  );
  const scenarioName = inferAgroScenarioName(scenario);
  const scenarioPlaybook = getAgroScenarioPlaybook(scenarioName);
  const rawScenarioTerms = analysis.specificTerms.slice(0, 3).join(', ');
  const sanitizeSpinLines = (lines?: string[]): string[] =>
    (lines ?? []).filter((line) => !/no cenario apresentado|no cenário apresentado/i.test(normalizeText(line)));
  const contextDrivenSituacao = scenarioIntent.contextSignal
    ? 'Hoje, quando essa decisão chega na mesa, o que mais pesa para ela andar: confiança, preço, urgência ou expectativa de resultado?'
    : '';
  const challengeDrivenSituacao = rawScenarioTerms
    ? `Quando entram temas como ${rawScenarioTerms}, como vocês organizam a decisão para não cair em comparação superficial de preço?`
    : '';

  const situacao = composeUnique(
    [
      anchors.situacao,
      ...(contextDrivenSituacao ? [contextDrivenSituacao] : []),
      ...(challengeDrivenSituacao ? [challengeDrivenSituacao] : []),
      ...scenarioDrivenPrompts.situacao,
      `Quando você precisa defender ${themeFocus} no cliente, como essa escolha costuma ser feita hoje na prática?`,
      ...(sanitizeSpinLines(scenarioPlaybook?.situacao)),
      ...(sanitizeSpinLines(spinTemplate?.situacao)),
      ...byTheme.situacao,
      ...byTerms.situacao,
      `Hoje, qual critério realmente pesa na decisão: preço imediato, previsibilidade de resultado ou risco operacional da ${context.nome}?`,
      `Quais números vocês usam para saber se a decisão está funcionando no negócio (${metricFocus})?`,
      'Quando há divergência entre técnico, compras e financeiro, qual critério final prevalece?',
      'Em qual etapa do ciclo atual vocês estão mais expostos a erro caro de decisão?',
      'Se eu pedir para resumir o objetivo desta reunião em uma meta de negócio, qual seria?' 
    ],
    4
  );

  const problema = composeUnique(
    [
      ...scenarioDrivenPrompts.problema,
      ...problemDeepDivePrompts,
      ...(sanitizeSpinLines(scenarioPlaybook?.problema)),
      ...(sanitizeSpinLines(spinTemplate?.problema)),
      anchors.problema,
      ...byTheme.problema,
      ...byTerms.problema,
      'Onde o foco em preço tem gerado custo que não aparece na planilha de compra?',
      'Qual gargalo hoje impede transformar esforço operacional em margem real no fechamento da safra?',
      'Que parte da operação tem maior variabilidade e mais impacto negativo quando a decisão é postergada?',
      'O que já foi tentado para resolver esse ponto e por que ainda não gerou estabilidade?',
      'Em que momento vocês percebem que “economizaram na compra, mas perderam no resultado”?' 
    ],
    4
  );

  const implicacao = composeUnique(
    [
      ...(scenarioIntent.desiredShift
        ? [`Se a operação não avançar para ${scenarioIntent.desiredShift}, qual impacto prático aparece em margem, risco e previsibilidade?`]
        : []),
      ...scenarioDrivenPrompts.implicacao,
      ...implicationEscalationPrompts,
      ...quantifiedImplications,
      ...(sanitizeSpinLines(scenarioPlaybook?.implicacao)),
      ...(sanitizeSpinLines(spinTemplate?.implicacao)),
      anchors.implicacao,
      ...byTheme.implicacao,
      ...byTerms.implicacao,
      'Se esse padrão continuar por mais um ciclo, quanto de margem líquida e previsibilidade de caixa pode deixar de aparecer?',
      'Qual risco operacional aumenta quando a decisão é tomada tarde ou por critério incompleto, e quanto isso pode custar na prática?',
      'Quanto custa para o negócio manter um critério de compra desalinhado com o objetivo econômico da safra?',
      'Qual oportunidade de resultado deixa de ser capturada, em sacas, margem ou caixa, quando essa dor não é tratada agora?',
      'Se nada mudar, qual meta estratégica fica mais vulnerável neste ano?' 
    ],
    4
  );

  const needPayoffPool = [
    ...(scenarioIntent.desiredCommitment
      ? [`Para viabilizar ${scenarioIntent.desiredCommitment}, qual condição de resultado precisa ficar explícita para você decidir agora?`]
      : []),
    ...scenarioDrivenPrompts.necessidade,
    ...(sanitizeSpinLines(scenarioPlaybook?.necessidade)),
    ...(sanitizeSpinLines(spinTemplate?.necessidadeSolucao)),
    anchors.necessidadeSolucao,
    ...(productFocus?.ganchosSpin.necessidadeSolucao ?? []),
    ...playerHooks.spinNeed,
    ...byTheme.necessidadeSolucao,
    ...byTerms.necessidadeSolucao,
    'Quais evidências e números você precisa ter em mãos para defender internamente esta recomendação e avançarmos hoje?',
    'Que condição mínima de resultado você considera justa para aprovar o próximo passo desta proposta?',
    'Qual formato de validação te deixa confortável para seguir: talhão piloto com data de leitura ou implantação por etapas?',
    'Qual regra de decisão vamos formalizar agora para evitar retorno à comparação apenas por preço?',
    'Quem precisa validar junto com você para sairmos desta reunião com encaminhamento definido?'
  ];

  if (analysis.stakeholders === 'multiplos') {
    needPayoffPool.unshift(
      'Como estruturamos uma proposta que compras consiga defender por custo total e o técnico consiga defender por resultado de campo?'
    );
  }

  if (desafiosAgro[0]) {
    needPayoffPool.unshift(
      `Considerando ${desafiosAgro[0].titulo.toLowerCase()}, qual avanço concreto podemos formalizar hoje para proteger resultado e margem?`
    );
  }

  return {
    situacao: situacao.map((q) => openSpinQuestion(q)),
    problema: problema.map((q) => openSpinQuestion(q)),
    implicacao: implicacao.map((q) => openSpinQuestion(q)),
    necessidadeSolucao: pick(composeUnique(needPayoffPool, 5), 4)
  };
};

const buildEpa = (
  analysis: ScenarioAnalysis,
  desafiosAgro: AgroChallengeSignal[],
  playerHooks: StrategicHooks,
  productFocus: ProductIntelligence | undefined,
  scenario: string,
  spin: SpinBlock,
  opc: PreparationResult['opc']
) => {
  void spin;
  void desafiosAgro;
  const context = CULTURA_CONTEXT[analysis.cultura];
  const conversationTheme = resolveConversationTheme(analysis, productFocus);
  const themeFocus = describeThemeFocus(conversationTheme);
  const scenarioName = inferAgroScenarioName(scenario);
  const scenarioPlaybook = getAgroScenarioPlaybook(scenarioName);
  const scenarioEpaPlaybook = getAgroScenarioEpaPlaybook(scenarioName);
  const spinTemplate = inferSpinScenarioTemplate(scenario);
  const epaTemplate = getEpaScenarioTemplateById(spinTemplate?.id ?? null);
  const normalizedScenario = normalizeText(scenario);
  const coopLoyaltyScenario = isCooperativeLoyaltyScenario(analysis, normalizedScenario);
  const currentCvaleRelationshipScenario = isCurrentCvaleRelationshipScenario(analysis, normalizedScenario);
  const produtoContexto = productFocus ? `${productFocus.produto} (${productFocus.fabricante})` : null;
  const { challengeFocus } = buildScenarioExecutionContext(
    analysis,
    scenario,
    productFocus
  );
  const scenarioIntent = extractScenarioIntent(scenario);
  const customerPainFocus = buildCustomerPainFocus(analysis, normalizedScenario, challengeFocus, scenarioIntent);
  const opcCompromisso = opc.compromisso[0] ?? 'formalizar próximo passo objetivo';
  const spinDiagnosisResumo = 'o ponto que mais trava resultado na operação';
  const spinImplicationResumo = `impacto em ${metricByCulture(analysis.cultura)} e risco operacional`;
  const spinNeedResumo =
    scenarioIntent.desiredCommitment ?? 'próximo passo com critério claro de decisão';
  const genericEpaPattern = /no cenario de decisao tecnico[-\s]?economica da operacao|janela da safra mais apertada/i;
  const toActionList = (items: string[], limit: number): string[] =>
    pick(composeUnique(items, Math.max(limit + 1, 6)), limit).map((item) => {
      const trimmed = item.trim();
      const withoutActionLabel = trimmed
        .replace(/^fa[çc]a isso(?: no [^:]+)?:\s*/i, '')
        .replace(/^mostre ao produtor:\s*/i, '')
        .trim();
      if (!withoutActionLabel) return trimmed;
      const base = withoutActionLabel.replace(/[.!?]+$/g, '');
      return `${base.charAt(0).toUpperCase()}${base.slice(1)}.`;
    });
  const produtoEducarHint = produtoContexto
    ? `${produtoContexto}: destaque como essa recomendação protege resultado, reduz risco e sustenta margem no ciclo.`
    : `Destaque como o ajuste em ${themeFocus} pode proteger resultado, reduzir risco e sustentar margem no ciclo.`;

  const educarScenarioInsights = [
    `Diferencie a conversa mostrando como ${customerPainFocus} cobra a conta no resultado da safra.`,
    `Diferencie a conversa de preço para valor total, mostrando o impacto de ${challengeFocus} no resultado final da safra.`,
    `Conecte o diagnóstico da operação aos números de negócio: ${metricByCulture(analysis.cultura)}.`,
    'Traga um insight novo que mude critério de decisão, sem repetir as perguntas da etapa de descoberta.',
    'Retome o que já foi validado com o produtor e transforme em direção prática para o próximo passo.'
  ];

  if (coopLoyaltyScenario) {
    return {
      educar: toActionList(
        composeUnique(
          [
            'Mostre ao produtor, com respeito, o custo invisível de manter 100% da decisão em um único fornecedor sem validação comparativa.',
            'Traga um insight novo: fidelidade sem métrica pode preservar conforto, mas pode esconder perda de margem e oportunidade.',
            `Use o diagnóstico já validado para provocar reflexão: ${spinDiagnosisResumo}.`,
            'Conecte o ensino à decisão central da reunião e ao ganho que o produtor pode capturar sem assumir risco desnecessário.',
            'Explique claramente como uma entrada controlada (ex.: sementes) reduz risco e aumenta qualidade de decisão.'
          ],
          5
        ),
        5
      ),
      personalizar: toActionList(
        composeUnique(
          [
            'Adapte a conversa ao perfil do produtor: primeiro reconheça os pontos fortes da cooperativa atual, depois explore lacunas sem confronto.',
            'Faça perguntas abertas específicas sobre o que ele valoriza e o que falta no atendimento atual para personalizar a proposta C.Vale.',
            `Personalize com base nas implicações já reconhecidas: ${spinImplicationResumo}.`,
            'Conecte a proposta ao que o produtor realmente valoriza, ao que ele teme perder e ao que ele toparia testar sem ruptura.'
          ],
          4
        ),
        4
      ),
      assumirControle: toActionList(
        composeUnique(
          [
            'Defina agenda objetiva de decisão: o que será testado, em qual área, por quanto tempo e com qual indicador.',
            'Mantenha firmeza com respeito: não entre em debate de opinião; puxe para critério de negócio e validação no campo.',
            `Converta a necessidade declarada no SPIN em compromisso claro: ${spinNeedResumo}.`,
            `Feche com o compromisso específico do OPC: ${opcCompromisso}.`
          ],
          4
        ),
        4
      )
    };
  }

  if (currentCvaleRelationshipScenario) {
    return {
      educar: toActionList(
        composeUnique(
          [
            'Mostre ao produtor que procurar a C.Vale apenas quando o problema aparece reduz a chance de prevenir perda e capturar resultado melhor.',
            'Traga um insight simples: quando a cooperativa entra antes, a decisão tende a ficar mais segura, mais previsível e menos reativa.',
            'Use o que já funciona hoje na relação com a C.Vale como ponto de apoio para ampliar a conversa sem parecer ruptura.',
            'Explique como planejamento mínimo, rotina de acompanhamento e decisão antecipada podem evitar retrabalho e improviso.',
            'Conecte o valor da C.Vale não só ao produto, mas à presença técnica, execução e segurança para o cooperado.'
          ],
          5
        ),
        5
      ),
      personalizar: toActionList(
        composeUnique(
          [
            'Adapte a conversa aos momentos em que o produtor já costuma buscar a C.Vale e mostre como antecipar esse apoio.',
            'Personalize a proposta com base no ponto em que a cooperativa hoje entra tarde demais e perde espaço de prevenção.',
            'Conecte a recomendação ao manejo específico que mais sofre quando a decisão fica para a última hora.',
            'Mostre um formato simples de acompanhamento para a C.Vale ganhar espaço sem aumentar complexidade para o produtor.'
          ],
          4
        ),
        4
      ),
      assumirControle: toActionList(
        composeUnique(
          [
            'Defina um próximo passo concreto em que a C.Vale entre antes do problema aparecer.',
            `Feche com um compromisso viável para este caso: ${opcCompromisso}.`,
            'Conduza o fechamento para um teste, programa mínimo ou rotina prática já com responsável e data de retorno.',
            'Mantenha a conversa ancorada em prevenção, resultado e constância, não apenas em reação ao problema do momento.'
          ],
          4
        ),
        4
      )
    };
  }

  const educarCoreByTheme: Record<Theme, string[]> = {
    adubo: [
      'Eduque o produtor com duas leituras da mesma decisão: custo de compra versus custo total do ciclo por hectare.',
      'Mostre como eficiência nutricional bem posicionada reduz perda invisível e protege produtividade.',
      'Evangelize por critério: adubo bom é o que converte em margem e previsibilidade, não o de menor preço por tonelada.'
    ],
    defensivo: [
      'Eduque o produtor mostrando que defensivo deve ser decidido por risco evitado e estabilidade de controle, não por preço por litro.',
      'Explique que janela, posicionamento e qualidade de aplicação pesam tanto quanto o produto para proteger resultado.',
      'Evangelize com lógica simples: manejo correto hoje evita retrabalho, perda de produtividade e pressão maior amanhã.'
    ],
    semente: [
      'Eduque com o conceito de genética por ambiente: a semente certa reduz risco e protege potencial da área.',
      'Mostre que decisão por preço da semente pode custar caro em stand, uniformidade e teto produtivo.',
      'Evangelize por resultado: escolha de material deve nascer da meta de produtividade e margem.'
    ],
    biologico: [
      'Eduque com protocolo: biológico sem posicionamento claro vira custo; com método vira consistência.',
      'Mostre como o biológico bem aplicado protege estabilidade fisiológica e reduz risco de oscilação.',
      'Evangelize por validação prática: área piloto, métrica simples e decisão baseada em evidência.'
    ],
    irrigacao: [
      'Eduque por eficiência hídrica e energética: irrigação boa é a que entrega resultado com previsibilidade.',
      'Mostre que precisão de manejo reduz desperdício e protege teto produtivo em fase crítica.',
      'Evangelize com rotina de decisão: quando irrigar, quanto irrigar e qual impacto esperado no negócio.'
    ],
    maquinario: [
      'Eduque mostrando que máquina de maior valor agregado se paga na execução: menos falha, menos sobreposição, mais eficiência.',
      'Mostre que tecnologia embarcada só gera valor quando vira rotina de campo e disciplina de operação.',
      'Evangelize por produtividade operacional: qualidade de execução também é decisão de margem.'
    ],
    servicos: [
      'Eduque que serviço técnico de valor acelera decisão certa e reduz erro caro no campo.',
      'Mostre como acompanhamento de execução converte recomendação em resultado real no talhão.',
      'Evangelize por governança simples: indicador, responsável e prazo para cada avanço.'
    ],
    geral: [
      'Eduque por negócio: decisão agrícola boa é a que combina técnica, execução e retorno por hectare.',
      'Mostre o custo da inércia em fases críticas da safra e o impacto na margem final.',
      'Evangelize com linguagem de campo e número de negócio, sem complicar.'
    ]
  };

  const educar = composeUnique(
    [
      `Diferencie o critério de decisão: mostre por que manter o modelo atual em ${challengeFocus} tende a esconder custo e risco.`,
      `Conecte o tema técnico ao problema real do produtor: ${customerPainFocus}.`,
      `Conecte o tema técnico com impacto econômico, focando em ${metricByCulture(analysis.cultura)}.`,
      'Traga um insight de negócio que provoque reflexão sem confronto e sem discurso genérico.',
      'Retome a direção da conversa com clareza e leve o produtor de volta para a decisão central do encontro.',
      produtoEducarHint,
      ...playerHooks.epaEducar,
      ...educarCoreByTheme[conversationTheme],
      'Use lógica Challenger de Ensino Comercial: insight novo, quebra de suposição e clareza do custo da inação.',
      `Mostre ao produtor que decisões em ${themeFocus} devem ser comparadas por retorno no ciclo completo, não por preço unitário.`,
      analysis.specificTerms.length
        ? `Use os itens citados no cenário (${analysis.specificTerms.slice(0, 3).join(', ')}) para demonstrar custo oculto, risco e ganho potencial.`
        : 'Use exemplos da operação real para demonstrar custo oculto, risco e ganho potencial.',
      ...educarScenarioInsights,
      ...(scenarioPlaybook?.epaEducar ? [scenarioPlaybook.epaEducar] : []).filter((x) => !genericEpaPattern.test(x)),
      ...(scenarioEpaPlaybook?.educar ? [scenarioEpaPlaybook.educar] : []).filter((x) => !genericEpaPattern.test(x)),
      ...(epaTemplate?.educarReframe ?? []).filter((x) => !genericEpaPattern.test(x)),
      `Evidencie que “preço baixo” pode mascarar custo alto no ciclo: decisão ruim cobra a conta em produtividade, margem e risco na ${context.nome}.`,
      'Apresente o custo da inércia: adiar decisão em janela crítica costuma custar mais do que discutir investimento corretamente no momento certo.',
      `Mostre oportunidade invisível: ${context.oportunidade} pode ser capturada com ajuste de critério e execução, sem aumentar complexidade desnecessária.`
    ],
    5
  );

  const personalizar = composeUnique(
    [
      ...(scenarioIntent.desiredShift
        ? [`Adapte a conversa para viabilizar o avanço desejado: ${scenarioIntent.desiredShift}.`]
        : []),
      `Adapte a proposta para atacar o ponto crítico do caso: ${customerPainFocus}.`,
      'Personalize a recomendação para a realidade da fazenda, da equipe e da janela da safra.',
      `Conecte argumento e proposta ao impacto esperado no negócio: ${spinImplicationResumo}.`,
      'Mantenha coerência com o que apareceu no diagnóstico, sem voltar para discurso padrão de produto.',
      'Ajuste a conversa ao perfil do decisor principal: para técnico, detalhe mecanismo de resultado; para financeiro, detalhe impacto em margem e risco.',
      'Ajuste linguagem por decisor e reduza fricções de compra, como tempo, esforço e incerteza.',
      ...(scenarioPlaybook?.epaPersonalizar ? [scenarioPlaybook.epaPersonalizar] : []).filter((x) => !genericEpaPattern.test(x)),
      ...(scenarioEpaPlaybook?.personalizar ? [scenarioEpaPlaybook.personalizar] : []).filter((x) => !genericEpaPattern.test(x)),
      ...(epaTemplate?.personalizarTailor ?? []).filter((x) => !genericEpaPattern.test(x)),
      'Conecte a recomendação ao momento da safra e à capacidade real da equipe para evitar proposta bonita no papel e fraca na execução.',
      analysis.primaryTheme === 'maquinario'
        ? 'Em maquinário, personalize por maturidade operacional: nível de treinamento da equipe, padrão de uso da tecnologia embarcada e disciplina de dados.'
        : 'Personalize a recomendação à rotina de campo da fazenda para facilitar execução e adoção real.',
      'Em conta estratégica, personalize pela prioridade dominante do cliente: proteção de margem, previsibilidade, expansão ou defesa competitiva.',
      'Se houver resistência, reduza debate opinativo e proponha validação prática com critério acordado.'
    ],
    4
  );

  if (productFocus) {
    personalizar.unshift(
      `No caso de ${productFocus.produto}, personalize a defesa por execução correta, janela e impacto real no sistema produtivo do cliente.`
    );
  }

  const takeControl = composeUnique(
    [
      ...(scenarioIntent.desiredCommitment
        ? [`Conduza o fechamento para um compromisso claro: ${scenarioIntent.desiredCommitment}.`]
        : []),
      `Assuma o controle da conversa em torno do problema central: ${customerPainFocus}.`,
      'Conduza com assertividade respeitosa e mantenha foco em resultado, avanço concreto e próximo passo viável.',
      `Converta a necessidade validada em critério objetivo de decisão: ${spinNeedResumo}.`,
      `Feche com um compromisso viável para este caso: ${opcCompromisso}.`,
      'Abra com agenda e tese de negócio, e peça acordo explícito para conduzir a conversa por impacto no resultado final.',
      ...(scenarioPlaybook?.epaControle ? [scenarioPlaybook.epaControle] : []).filter((x) => !genericEpaPattern.test(x)),
      ...(scenarioEpaPlaybook?.assumirControle ? [scenarioEpaPlaybook.assumirControle] : []).filter((x) => !genericEpaPattern.test(x)),
      ...(epaTemplate?.assumirControleTakeControl ?? []).filter((x) => !genericEpaPattern.test(x)),
      'Redirecione objeção de preço para custo total, risco e retorno esperado da decisão.',
      'Sustente tensão construtiva com respeito para evitar estagnação na indecisão.',
      'Faça paradas curtas de alinhamento durante a reunião para manter direção e evitar dispersão.',
      'Feche com síntese objetiva: decisão em aberto, critério acordado, responsável e prazo do próximo passo.'
    ],
    4
  );

  return {
    educar: toActionList(educar, 5),
    personalizar: toActionList(personalizar, 4),
    assumirControle: toActionList(takeControl, 4)
  };
};

const buildTips = (
  analysis: ScenarioAnalysis,
  desafiosAgro: AgroChallengeSignal[],
  playerHooks: StrategicHooks,
  productFocus?: ProductIntelligence
): string[] => {
  const manufacturerNotes = buildManufacturerNotes(analysis);
  const desafiosTips = desafiosAgro.map(
    (item) => `Traga para a conversa o desafio "${item.titulo.toLowerCase()}" e conecte com a realidade da fazenda: ${item.comoUsarNaConversa}`
  );
  const tipsPool = [
    ...playerHooks.tips,
    'Foco na experiência do associado: simplifique logística e burocracia da decisão para tornar a execução mais fácil no dia a dia.',
    'Segurança em primeiro lugar: em recomendações de aplicação, sempre orientar EPI, procedimento correto e respeito ambiental.',
    'Credibilidade inegociável: não propor venda casada sem base técnica; recomendar o que é correto para a gleba e o objetivo da safra.',
    'Abordagem educativa: atuar como instrutor em campo, explicando adoção prática de novas tecnologias (biológicos, telemetria e precisão).',
    'Mostre o mesmo tema em dois números simples: preço de compra e custo total por hectare no fim da safra.',
    'Quando a conversa travar em preço, puxe para resultado: produtividade, risco operacional, margem e previsibilidade.',
    'Traduza o técnico para o negócio da fazenda: o que muda na lavoura e o que muda no caixa.',
    'Ao ouvir “está caro”, traga comparação prática: custo do item versus custo de uma decisão mal tomada na janela da safra.',
    'Feche cada conversa com avanço concreto: data, responsável, indicador e regra de decisão.',
    'Evite sair com “vamos pensar”: proponha um próximo passo curto, mensurável e com dono definido.',
    'No corpo da conversa, priorize perguntas abertas. Deixe perguntas fechadas para confirmação final de compromisso.',
    ...desafiosTips
  ];

  if (productFocus) {
    tipsPool.unshift(
      `Se o foco for ${productFocus.produto}, conecte argumento técnico a impacto de negócio: ${productFocus.propostaValorCurta.toLowerCase()}`
    );
  }

  if (analysis.tags.includes('cooperativa')) {
    tipsPool.unshift('Em cooperativa, ancore o argumento em valor para cooperado e sustentabilidade econômica da operação como um único racional.');
  }

  if (analysis.tags.includes('resistencia')) {
    tipsPool.unshift('Com cliente resistente, troque convencimento por validação: teste curto, métrica clara e decisão baseada em evidência.');
  }

  if (analysis.specificTerms.length) {
    tipsPool.unshift(
      `Use no roteiro os termos do cenário (${analysis.specificTerms.slice(0, 4).join(', ')}) para mostrar domínio do contexto e evitar conversa genérica.`
    );
  }

  if (analysis.primaryTheme === 'maquinario') {
    tipsPool.unshift(
      'Em máquinas e implementos de maior valor agregado, não pare no hardware: mostre o valor da tecnologia embarcada (taxa variável, telemetria, piloto, corte de seção) no resultado final.'
    );
  }

  if (analysis.foco === 'preco' || analysis.prioridade === 'competicao') {
    tipsPool.unshift('Quando houver travamento por preço, leve opção de fechamento em área menor e, se couber, proposta via barter.');
  }

  return pick(composeUnique([...manufacturerNotes, ...tipsPool], 9), 6);
};

const buildNextStep = (analysis: ScenarioAnalysis): string => {
  if (analysis.etapa === 'primeiro_contato') {
    return 'Próximo passo sugerido: agendar diagnóstico de 45 minutos com dados de campo e financeiros para decidir em até 7 dias.';
  }

  if (analysis.prioridade === 'competicao') {
    return 'Próximo passo sugerido: montar comparação simples de custo total e risco por alternativa, com reunião de decisão já agendada.';
  }

  if (analysis.prioridade === 'expansao') {
    return 'Próximo passo sugerido: fechar teste em talhão ou área menor, com meta de resultado e data para decidir expansão.';
  }

  if (analysis.prioridade === 'retencao') {
    return 'Próximo passo sugerido: revisar resultado entregue, identificar nova alavanca de valor e pactuar plano de evolução com metas do próximo ciclo.';
  }

  return 'Próximo passo sugerido: sair da reunião com plano de ação de curto prazo, dono por atividade e indicador de resultado para acompanhamento.';
};

const buildRoiDefenseHooks = (
  analysis: ScenarioAnalysis,
  playerHooks: StrategicHooks,
  productFocus?: ProductIntelligence
): string[] => {
  const scenarioRoiAnchor = `No ROI deste cenário, mostre retorno por hectare e redução de risco operacional na ${CULTURA_CONTEXT[analysis.cultura].nome}.`;
  const filteredPlayerRoiHooks = playerHooks.roiHooks
    .filter((hook) => !/no roi c\.?vale/i.test(normalizeText(hook)))
    .map((hook) => hook.replace(/no roi c\.?vale,?\s*/i, 'No ROI deste cenário, '));

  const hooks = [
    scenarioRoiAnchor,
    ...filteredPlayerRoiHooks,
    'Defina com o produtor uma regra de decisão simples: manter manejo atual versus avançar, com base em ganho econômico por hectare.',
    'Leve 3 números para defender valor: receita adicional potencial, custo evitado e prazo de retorno da mudança.',
    'Mostre que ROI não é planilha bonita: é resultado medido em talhão, com critério acordado antes da implantação.'
  ];

  if (analysis.primaryTheme === 'maquinario') {
    hooks.unshift('Em máquinas e implementos, inclua no ROI o valor da execução: menos sobreposição, menos falha e melhor uso da tecnologia embarcada.');
  }

  if (productFocus) {
    hooks.unshift(`No caso de ${productFocus.produto}, traduza benefício técnico em retorno econômico medido na área piloto.`);
  }

  return pick(composeUnique(hooks, 5), 3);
};

const SEARCH_DOMAINS_BY_PLAYER = [
  'corteva.com.br',
  'yara.com.br',
  'syngenta.com.br',
  'cropscience.bayer.com.br',
  'mosaicco.com.br',
  'icl-growingsolutions.com',
  'ihara.com.br',
  'pioneer.com',
  'brevant.com.br',
  'dekalb.com.br',
  'nksementes.com.br',
  'kws.com',
  'coamo.com.br',
  'cocamar.com.br',
  'cvale.com.br',
  'copacol.com.br'
];

const buildValueSineMap = (params: {
  objectives: string[];
  insight: string;
  risk: string;
  valueProposition: string;
  solution: string;
  specificTerms: string[];
  roiHooks: string[];
  scenarioIntent?: ScenarioIntent;
  productSalesChallenge?: string;
}): ValueSineMap => {
  const { objectives, insight, risk, valueProposition, solution, specificTerms, roiHooks, scenarioIntent, productSalesChallenge } = params;
  const naturalizeParentheses = (value: string): string =>
    value
      .replace(/\s*\(([^)]+)\)/g, ', $1')
      .replace(/\s*\|\s*/g, ', ')
      .replace(/\s+,/g, ',')
      .replace(/,\s*,/g, ', ')
      .replace(/\s+/g, ' ')
      .trim();
  const simplifyObjectiveLine = (value: string): string =>
    naturalizeParentheses(value)
      .replace(/^objetivo central deste cenario:\s*/i, '')
      .replace(/,?\s*dentro do contexto real:\s*/i, ', ')
      .replace(/[.]+$/g, '')
      .trim();
  const cleanTerm = (value: string): string =>
    naturalizeParentheses(value).replace(/\s+/g, ' ').trim();
  const compactText = (value: string, max = 160): string => {
    const cleaned = naturalizeParentheses(value).replace(/\.\.\./g, '.').replace(/\s+/g, ' ').trim();
    if (cleaned.length <= max) return cleaned;
    const slice = cleaned.slice(0, max);
    const safe = slice.slice(0, Math.max(slice.lastIndexOf('.'), slice.lastIndexOf(','), slice.lastIndexOf(' ')));
    return `${safe.trim()}.`;
  };
  const uniqueTerms = unique(specificTerms.map(cleanTerm).filter(Boolean));
  const termFocus =
    uniqueTerms.length > 0
      ? `Termos-chave a considerar: ${uniqueTerms.slice(0, 4).join(', ')}.`
      : 'Termos-chave: usar linguagem específica da operação.';
  const objectiveFocus = simplifyObjectiveLine(objectives[0] ?? 'elevar a conversa para decisão orientada por valor');
  const conciseInsight = compactText(insight, 150);
  const conciseRisk = compactText(risk, 140);
  const conciseValueProposition = compactText(valueProposition, 170);
  const conciseSolution = compactText(solution, 170);
  const intentShift = cleanIntentChunk(scenarioIntent?.desiredShift);
  const intentCommitment = cleanIntentChunk(scenarioIntent?.desiredCommitment);
  const intentPain = normalizePainSignal(scenarioIntent?.painSignal ?? null);
  const intentContext = cleanIntentChunk(scenarioIntent?.contextSignal);
  const intentFocusLine = intentContext
    ? `Contexto da conversa: ${intentContext}.`
    : 'Contexto da conversa: operação real da propriedade.';
  const intentPainLine = intentPain
    ? `Ponto crítico a tratar: ${intentPain}.`
    : 'Ponto crítico a tratar: remover gargalos que travam resultado e margem.';
  const intentShiftLine = intentShift
    ? `Leve a conversa para ${intentShift}.`
    : 'Leve a conversa do preço isolado para decisão por valor total.';
  const intentCommitmentLine = intentCommitment
    ? `Feche com um compromisso claro: ${intentCommitment}.`
    : 'Feche com um próximo passo claro, com prazo e responsável.';
  const salesChallengeLine = productSalesChallenge
    ? `Desafio comercial do produto em foco: ${productSalesChallenge}.`
    : 'Desafio comercial do produto em foco: defender valor por resultado e não por preço.';

  const map: ValueSineMap = {
    titulo: 'Mapa Estratégico da Conversa — Senoide da Venda de Valor',
    subtitulo:
      'Visualize como conduzir a conversa comercial de forma estruturada, conectando OPC, SPIN e EPA ao longo dos seis passos da venda de valor.',
    steps: [
      {
        id: 1,
        titulo: 'Estabelecer o Cenário',
        resumo: 'Diálogo colaborativo e construção de credibilidade',
        explicacao:
          'Comece pelo contexto da operação e pelo objetivo da safra. Demonstre escuta ativa, linguagem simples e domínio técnico do campo para gerar confiança.',
        guiaTatico: [
          'Abra com 2 minutos de contexto do cliente antes de falar de solução.',
          'Use perguntas abertas para entender meta, gargalo e impacto econômico.',
          `Combine agenda curta: cenário, impacto, próximo passo. ${termFocus}`,
          intentFocusLine,
          salesChallengeLine
        ],
        sugestaoPersonalizada:
          `Comece conectando contexto e objetivo de negócio para esta conversa: ${objectiveFocus}. Assim você cria confiança e prepara o reposicionamento do problema.`
      },
      {
        id: 2,
        titulo: 'Reestruturação do Problema',
        resumo: 'Trazer novo ângulo',
        explicacao:
          'Reposicione o problema sem confronto. Mostre um ângulo que o cliente ainda não conectou entre decisão de compra e resultado da fazenda.',
        guiaTatico: [
          'Traga um novo ângulo que mude o critério de preço para custo total.',
          'Conecte esse novo ângulo à realidade da propriedade, sem discurso genérico.',
          'Valide entendimento com linguagem objetiva de campo.',
          intentShiftLine,
          salesChallengeLine
        ],
        sugestaoPersonalizada:
          `Use este ponto-chave para mudar o critério da conversa de preço para resultado: ${conciseInsight} Isso abre espaço para evidência no impacto racional.`
      },
      {
        id: 3,
        titulo: 'Impacto Racional',
        resumo: 'Intensificação do problema',
        explicacao:
          'Mostre dado concreto para provar o tamanho do problema. Use indicador técnico e indicador econômico para dar robustez à conversa.',
        guiaTatico: [
          'Leve 2 evidências: uma de produtividade e outra de margem/risco.',
          'Use comparação simples: cenário atual versus cenário corrigido.',
          'Evite excesso de números; destaque só os que mudam decisão.',
          intentPainLine
        ],
        sugestaoPersonalizada:
          'Apresente uma prova técnica e uma prova econômica para sustentar a narrativa de valor e legitimar a urgência da mudança.'
      },
      {
        id: 4,
        titulo: 'Impacto Emocional',
        resumo: 'Humanizar o problema e gerar urgência',
        explicacao:
          'Traga o risco de manter tudo como está para o presente: perda de janela, pressão de caixa, retrabalho ou custo de oportunidade. Gere urgência com responsabilidade.',
        guiaTatico: [
          'Mostre o custo de adiar decisão em termos de risco operacional.',
          'Conecte urgência a um marco real da safra, como janela, prazo ou previsão climática.',
          'Mantenha tom consultivo: firmeza sem pressão agressiva.',
          intentShiftLine
        ],
        sugestaoPersonalizada:
          `Transforme o risco central em urgência prática: ${conciseRisk} Mostre o custo da inação para preparar aceitação da proposta de valor.`
      },
      {
        id: 5,
        titulo: 'Proposta de Valor',
        resumo: 'Novo caminho para resolver o problema',
        explicacao:
          'Apresente um caminho claro de execução, conectando dor priorizada, critério de decisão e resultado esperado no negócio.',
        guiaTatico: [
          'Mostre proposta em 3 blocos: ação, impacto esperado e condição de execução.',
          'Conecte cada ponto da proposta a uma dor já validada.',
          'Fale em retorno e previsibilidade, não apenas em atributo.',
          roiHooks[0] ?? 'Amarre a proposta a uma métrica simples de ROI para sustentar a decisão.',
          intentCommitmentLine
        ],
        sugestaoPersonalizada:
          `Apresente a proposta como resposta direta ao que foi construído antes: ${conciseValueProposition} Conecte com retorno, previsibilidade e redução de risco.`
      },
      {
        id: 6,
        titulo: 'Pacote de Soluções',
        resumo: 'Sua solução: apresentação das soluções de valor agregado',
        explicacao:
          'Apresente o pacote final conectando produto, serviço e suporte técnico àquilo que foi construído na conversa. Feche com compromisso claro.',
        guiaTatico: [
          'Apresente somente o pacote aderente ao problema priorizado.',
          'Defina responsável, prazo e indicador de validação da área piloto ou da implantação.',
          'Finalize com próximo passo já agendado.',
          roiHooks[1] ?? 'Feche com critério objetivo de ROI para sustentar a decisão do cliente.',
          intentCommitmentLine
        ],
        sugestaoPersonalizada:
          `Feche a narrativa com compromisso claro: ${conciseSolution} Garanta próximo passo com prazo, dono e critério econômico de decisão.`
      }
    ],
    fases: [
      { nome: 'Gancho', etapas: [1, 2] },
      { nome: 'Impacto', etapas: [3, 4] },
      { nome: 'Solução', etapas: [5, 6] }
    ]
  };

  return {
    ...map,
    steps: map.steps.map((step) => ({
      ...step,
      titulo: naturalizeParentheses(step.titulo),
      resumo: naturalizeParentheses(step.resumo),
      explicacao: naturalizeParentheses(step.explicacao),
      guiaTatico: step.guiaTatico.map((line) => naturalizeParentheses(line)),
      sugestaoPersonalizada: naturalizeParentheses(step.sugestaoPersonalizada)
    }))
  };
};

const normalizePreparationInput = (input: string | PreparationInput): PreparationInput => {
  if (typeof input === 'string') {
    return {
      producerProfile: 'analitico',
      scenario: input,
      culture: '',
      mainChallenge: '',
      visitGoal: ''
    };
  }

  return {
    producerProfile: input.producerProfile,
    scenario: input.scenario,
    culture: input.culture,
    mainChallenge: input.mainChallenge ?? '',
    visitGoal: input.visitGoal ?? ''
  };
};

const buildScenarioFromStructuredInput = (input: PreparationInput): string =>
  [
    input.scenario.trim(),
    input.culture.trim() ? `Cultura foco: ${input.culture.trim()}.` : '',
    (input.mainChallenge ?? '').trim() ? `Desafio principal do cliente: ${(input.mainChallenge ?? '').trim()}.` : '',
    (input.visitGoal ?? '').trim() ? `Objetivo da visita: ${(input.visitGoal ?? '').trim()}.` : ''
  ]
    .filter(Boolean)
    .join(' ');

const adaptLinesByProfile = (lines: string[], profile: ProducerBehaviorProfile, section: ProfileSection): string[] => {
  const profilePlaybook = PROFILE_PLAYBOOKS[profile];
  const base =
    section === 'sine'
      ? composeUnique([...profilePlaybook.sine, ...lines], Math.max(lines.length + 2, 6))
      : composeUnique(lines, Math.max(lines.length + 1, 6));

  if (profile === 'diretivo') {
    return base.map((line) =>
      line
        .replace(/ sem [^.]+/gi, '')
        .replace(/ de forma [^.]+/gi, '')
        .replace(/\s+/g, ' ')
        .trim()
    );
  }

  if (profile === 'integrador') {
    return base.map((line) =>
      line
        .replace(/^Feche\b/i, 'Construa junto')
        .replace(/^Conduza\b/i, 'Conduza com calma')
        .replace(/^Mostre\b/i, 'Mostre com segurança')
    );
  }

  if (profile === 'expressivo') {
    return base.map((line) =>
      line
        .replace(/^Leve\b/i, 'Leve com energia')
        .replace(/^Mostre\b/i, 'Mostre de forma envolvente')
        .replace(/^Conecte\b/i, 'Conecte com visão de futuro')
    );
  }

  return base;
};

const adaptSpinByProfile = (spin: SpinBlock, profile: ProducerBehaviorProfile): SpinBlock => {
  const playbook = PROFILE_PLAYBOOKS[profile];

  return {
    situacao: pick(composeUnique([...playbook.spinSituacao, ...spin.situacao], 6), 4),
    problema: pick(composeUnique([...playbook.spinProblema, ...spin.problema], 6), 4),
    implicacao: pick(composeUnique([...playbook.spinImplicacao, ...spin.implicacao], 6), 4),
    necessidadeSolucao: pick(composeUnique([...playbook.spinNecessidade, ...spin.necessidadeSolucao], 6), 4)
  };
};

const adaptValueSineMapByProfile = (map: ValueSineMap, profile: ProducerBehaviorProfile): ValueSineMap => {
  const playbook = PROFILE_PLAYBOOKS[profile];

  return {
    ...map,
    subtitulo: `${map.subtitulo} Perfil predominante da conversa: ${PROFILE_LABELS[profile]}.`,
    steps: map.steps.map((step) => ({
      ...step,
      guiaTatico: pick(composeUnique([...playbook.sine, ...step.guiaTatico], step.guiaTatico.length + 2), step.guiaTatico.length)
    }))
  };
};

const buildBehaviorApproach = (profile: ProducerBehaviorProfile): PreparationResult['abordagemPerfil'] => ({
  perfil: profile,
  titulo: PROFILE_PLAYBOOKS[profile].titulo,
  linguagem: PROFILE_PLAYBOOKS[profile].linguagem,
  postura: PROFILE_PLAYBOOKS[profile].postura,
  ritmo: PROFILE_PLAYBOOKS[profile].ritmo,
  fechamento: PROFILE_PLAYBOOKS[profile].fechamento
});

export const generatePreparation = async (input: string | PreparationInput): Promise<PreparationResult> => {
  const normalizedInput = normalizePreparationInput(input);
  const trimmedScenario = buildScenarioFromStructuredInput(normalizedInput).trim();

  if (!trimmedScenario) {
    throw new Error('Descreva o cenário da reunião para gerar a preparação.');
  }

  await new Promise((resolve) => setTimeout(resolve, 950));

  const analysis = analyzeScenario(trimmedScenario);
  const staticProducts = detectProductIntelligence(analysis.normalized, analysis.fabricantes);
  const dynamicProducts = buildDynamicProductProfiles(analysis, analysis.normalized, staticProducts);
  const produtosDetectados = pick([...staticProducts, ...dynamicProducts], 6);
  const produtoPrincipal = produtosDetectados[0];
  const playerHooks = buildStrategicHooksByPlayer(analysis, analysis.normalized, produtoPrincipal);
  const termosPossiveis = detectPotentialProductTerms(analysis.normalized);
  const trilhasPesquisaProdutos = termosPossiveis
    .filter((termo) => !produtosDetectados.some((p) => p.aliases.some((alias) => alias.test(termo))))
    .map((termo) => ({
      termo,
      sugestoes: SEARCH_DOMAINS_BY_PLAYER.slice(0, 6).map(
        (domain) => `https://www.google.com/search?q=${encodeURIComponent(`${termo} site:${domain}`)}`
      )
    }))
    .slice(0, 3);
  const desafiosAgro = selectAgroChallenges(analysis);
  const scenarioIntent = extractScenarioIntent(trimmedScenario);
  const spinBase = buildSpin(analysis, desafiosAgro, trimmedScenario, playerHooks, produtoPrincipal);
  const spin = adaptSpinByProfile(spinBase, normalizedInput.producerProfile);
  const opcBase = buildOpc(analysis, playerHooks, produtoPrincipal, trimmedScenario, spin);
  const opc = {
    objetivo: pick(adaptLinesByProfile(opcBase.objetivo, normalizedInput.producerProfile, 'opc'), 3),
    processo: pick(adaptLinesByProfile(opcBase.processo, normalizedInput.producerProfile, 'opc'), 6),
    compromisso: pick(adaptLinesByProfile(opcBase.compromisso, normalizedInput.producerProfile, 'opc'), 3)
  };
  const epaBase = buildEpa(analysis, desafiosAgro, playerHooks, produtoPrincipal, trimmedScenario, spin, opc);
  const epa = {
    educar: pick(adaptLinesByProfile(epaBase.educar, normalizedInput.producerProfile, 'epa'), 5),
    personalizar: pick(adaptLinesByProfile(epaBase.personalizar, normalizedInput.producerProfile, 'epa'), 4),
    assumirControle: pick(adaptLinesByProfile(epaBase.assumirControle, normalizedInput.producerProfile, 'epa'), 4)
  };
  const dicasPraticas = buildTips(analysis, desafiosAgro, playerHooks, produtoPrincipal);
  const proximoPasso = buildNextStep(analysis);
  const roiHooks = buildRoiDefenseHooks(analysis, playerHooks, produtoPrincipal);
  const engineInsights = buildEngineInsights(analysis, trimmedScenario, playerHooks, produtoPrincipal);
  const executionContext = buildScenarioExecutionContext(analysis, trimmedScenario, produtoPrincipal);
  const valueSineMap = adaptValueSineMapByProfile(
    buildValueSineMap({
      objectives: opc.objetivo,
      insight: epa.educar[0],
      risk: epa.educar[1] ?? epa.assumirControle[0],
    valueProposition: epa.personalizar[0],
    solution: opc.compromisso.join(' | '),
    specificTerms: [
      ...analysis.specificTerms,
      ...(produtoPrincipal ? [`${produtoPrincipal.produto} (${produtoPrincipal.fabricante})`] : [])
    ],
      roiHooks,
      scenarioIntent,
      productSalesChallenge: executionContext.productSalesChallenge
    }),
    normalizedInput.producerProfile
  );

  const produtosReferenciados: ProductReference[] = produtosDetectados.map((item) => ({
    produto: item.produto,
    fabricante: item.fabricante,
    propostaValorCurta: item.propostaValorCurta,
    fortalezasCompetitivas: item.fortalezasCompetitivas,
    cuidadosDePosicionamento: item.cuidadosDePosicionamento,
    fontes: item.fontes,
    atualizadoEm: item.atualizadoEm
  }));

  return {
    tituloFicha: `Ficha Estratégica de Preparação Comercial | ${APP_NAME}`,
    cenarioOriginal: trimmedScenario,
    abordagemPerfil: buildBehaviorApproach(normalizedInput.producerProfile),
    engineInsights,
    opc,
    spin,
    epa,
    valueSineMap,
    dicasPraticas,
    proximoPasso,
    metadata: {
      generatedAt: new Date().toISOString(),
      tags: analysis.tags,
      primaryTheme: analysis.primaryTheme,
      specificTerms: analysis.specificTerms,
      machineTechTerms: analysis.machineTechTerms,
      competitiveDifferentiationFocus:
        describeProductDifferentiation(produtoPrincipal) ?? describeCompetitiveDifferentiation(analysis),
      missingScenarioDetails: buildMissingScenarioDetails(analysis, produtosReferenciados.length > 0),
      fabricantesCitados: analysis.fabricantes,
      referenciasFabricantes: buildManufacturerReferences(analysis),
      referenciasSetoriais: buildSectorReferences(analysis),
      produtosReferenciados,
      trilhasPesquisaProdutos,
      desafiosAgro: desafiosAgro.map((item) => ({
        titulo: item.titulo,
        contexto: item.contexto,
        comoUsarNaConversa: item.comoUsarNaConversa,
        perguntaAberta: item.perguntaAberta,
        fontes: item.fontes
      })),
      prioridade: analysis.prioridade,
      producerProfile: normalizedInput.producerProfile,
      cultureInput: normalizedInput.culture,
      mainChallengeInput: normalizedInput.mainChallenge ?? '',
      visitGoalInput: normalizedInput.visitGoal ?? ''
    }
  };
};
